import Axios, { HttpStatusCode, Method } from 'axios';
import axiosInstance from './axios-instance';
import { handleTenantSuspended, isTenantSuspendedError } from 'src/utils/tenantSuspension';
import { SearchQueryFilters } from 'src/models/CommonInterface';
import { IAsset } from 'src/models/CommonInterface';

export function throwIfError(response: { status: number; data?: { message?: string | string[] } } | undefined): void {
  if (!response || !Number.isFinite(response.status) || response.status < 200 || response.status >= 300) {
    const message = response?.data?.message;
    throw new Error(Array.isArray(message) ? message.join(', ') : message || 'Request failed. Please try again.');
  }
}

// // Add these interfaces at the top of your file
// interface AssetResponse {
//   asset: {
//     id: number;
//     url: string;
//     assetId: string;
//   };
// }

// interface UploadResponse {
//   data: AssetResponse;
//   status: number;
// }

export class BaseAPI {
  static getExpenseByUserIdList(arg0: (result: any) => void) {
    throw new Error('Method not implemented.');
  }

  static getAPIBaseURI() {
    const BASE_API_URL = process.env.REACT_APP_API_URL;
    return BASE_API_URL;
  }

  static buildAPIURL(apiPath: string) {
    return `${BaseAPI.getAPIBaseURI()}${apiPath.startsWith('/') ? '' : '/'}${apiPath}`;
  }

  /**
   * Build auth headers for an API request.
   *
   * When no `access_token` is available in sessionStorage (the crelo SSO
   * cookie-session case — see reducers/authSlice.ts restoreSession — where
   * the JWT lives ONLY in the HttpOnly `hris_session` cookie and is never
   * exposed to JS), the `Authorization` header is simply OMITTED rather than
   * throwing. The request still authenticates: axiosInstance sends
   * `withCredentials: true` (see providers/axios-instance.ts), so the browser
   * attaches the `hris_session` cookie automatically for same-origin
   * requests, and the Go backend's JWT middleware accepts it as a fallback
   * token source when CRELO_SSO_ENABLED=true. Bearer-token users are
   * completely unaffected — this only changes behavior when there was no
   * token to begin with (previously a hard throw).
   */
  static getAuthHeaders(contentType: string) {
    const userDetailString = sessionStorage.getItem('UserDetail');
    const accessToken = userDetailString ? JSON.parse(userDetailString)?.access_token : null;

    const headers: Record<string, string> = {
      'Content-Type': contentType,
      'X-Tenant-Id': BaseAPI.getTenantIdFromToken() ?? ''
    };
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    return headers;
  }

  /**
   * `fetch` with the same auth as axiosInstance, for SWR fetchers and other
   * raw-fetch call sites: Bearer header when a token is in sessionStorage,
   * otherwise the HttpOnly hris_session cookie via `credentials: 'include'`
   * (cookie-session users have an empty access_token — see getAuthHeaders).
   *
   * Fails fast with 'Not authenticated' when there is no UserDetail at all
   * (logged out), rather than firing a request that can only 401, and refuses
   * any URL outside REACT_APP_API_URL so credentials never go to another host.
   */
  static async authFetch(url: string, init: RequestInit = {}): Promise<Response> {
    if (!sessionStorage.getItem('UserDetail')) {
      throw new Error('Not authenticated');
    }
    // The Bearer token and the session cookie are for our API only, never any other host.
    if (!BaseAPI.isApiUrl(url)) {
      throw new Error(`authFetch: not an API URL: ${url}`);
    }
    // Caller headers win; `init.headers` may be a plain object, an array or a Headers instance.
    const headers = new Headers(BaseAPI.getAuthHeaders('application/json'));
    new Headers(init.headers).forEach((value, key) => headers.set(key, value));

    const response = await fetch(url, { ...init, headers, credentials: 'include' });

    // Same as axiosInstance's interceptor: a suspended organisation ends the session for every
    // call. A 401 passes through, as it does there.
    if (response.status === 403) {
      try {
        const data = await response.clone().json();
        if (isTenantSuspendedError({ response: { status: 403, data } })) {
          handleTenantSuspended(true);
        }
      } catch {
        // not a JSON body: an ordinary 403, left to the caller
      }
    }
    return response;
  }

  /** True when `url` is under REACT_APP_API_URL (same origin and path prefix). */
  static isApiUrl(url: string): boolean {
    const base = process.env.REACT_APP_API_URL;
    if (!base) return false;
    try {
      const target = new URL(url, window.location.origin);
      const api = new URL(base, window.location.origin);
      const prefix = api.pathname.endsWith('/') ? api.pathname : `${api.pathname}/`;
      return target.origin === api.origin && (target.pathname === api.pathname || target.pathname.startsWith(prefix));
    } catch {
      return false;
    }
  }

  /**
   * Resolve the current tenant ID.
   *
   * Bearer-token users (access_token present in sessionStorage.UserDetail):
   * decoded straight out of the JWT payload, exactly as before.
   *
   * Cookie-only users (crelo SSO — see reducers/authSlice.ts restoreSession,
   * GET /auth/session): there is no readable access_token (the JWT lives
   * ONLY in the HttpOnly hris_session cookie), so UserDetail.access_token is
   * absent/empty. In that case fall back to UserDetail.tenantId, which
   * GET /auth/session always includes as a top-level field (see
   * backend-go usecases.ToSessionPayload) — this avoids ever needing to
   * decode a token client-side for the cookie-only path.
   *
   * Returns the tenant ID as a string, or null if neither source yields one.
   */
  static getTenantIdFromToken(): string | null {
    try {
      const userDetailString = sessionStorage.getItem('UserDetail');
      if (!userDetailString) return null;

      const userDetail = JSON.parse(userDetailString);
      const accessToken = userDetail?.access_token;

      if (!accessToken) {
        // Cookie-only session: no token to decode — use the tenantId the
        // /auth/session bootstrap already gave us.
        if (userDetail?.tenantId !== undefined && userDetail?.tenantId !== null) {
          return String(userDetail.tenantId);
        }
        return null;
      }

      // JWT structure: header.payload.signature
      const parts = accessToken.split('.');
      if (parts.length !== 3) return null;

      // Decode base64 payload
      const payload = parts[1];
      const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      const claims = JSON.parse(decodedPayload);

      // Extract tenant_id (could be tenantId or tenant_id in JWT)
      return claims.tenantId || claims.tenant_id || null;
    } catch (error) {
      console.error('Failed to resolve tenant ID:', error);
      return null;
    }
  }

  /**
   * Build a stable, publicly-accessible URL for a profile picture asset.
   * No auth required — secured by nanoID obscurity + tenant scoping.
   * Only assets uploaded with category='profile' are served by this endpoint.
   * Returns null when assetId is empty or tenant ID cannot be determined.
   */
  static buildProfilePictureURL(assetId: string | null | undefined): string | null {
    if (!assetId) return null;
    const tenantId = BaseAPI.getTenantIdFromToken();
    if (!tenantId) return null;
    return BaseAPI.buildAPIURL(`/public/t/${tenantId}/profile-picture/${assetId}`);
  }

  /**
   * Extract nanoID from a signed S3 URL and build the public streaming URL.
   * S3 path format: profile/<nanoID>-<filename> (nanoID is always NANO_ID_LENGTH chars).
   * NanoIDs in this project are 14 alphanumeric characters with embedded hyphens,
   * e.g. "AOW1bdW-ENtTDt". The S3 key filename starts with the nanoID followed by
   * a dash and the rest of the filename: "AOW1bdW-ENtTDt-timestamp.jpg".
   * Returns null if the URL can't be parsed, the extracted segment fails validation,
   * or the tenant ID is unavailable.
   */
  static buildProfilePictureURLFromSignedURL(signedUrl: string | null | undefined): string | null {
    /** Length of a nanoID as used in this project (e.g. "AOW1bdW-ENtTDt"). */
    const NANO_ID_LENGTH = 14;
    /** Valid nanoID characters: alphanumeric plus hyphens. */
    const NANO_ID_PATTERN = /^[A-Za-z0-9-]{14}$/;

    if (!signedUrl) return null;
    try {
      // Extract the path segment after the bucket host, e.g. /profile/AOW1bdW-ENtTDt-timestamp.jpg
      const url = new URL(signedUrl);
      // pathname = /profile/AOW1bdW-ENtTDt-timestamp.jpg (possibly percent-encoded)
      const filename = url.pathname.split('/').pop();
      if (!filename) return null;
      const decoded = decodeURIComponent(filename); // "AOW1bdW-ENtTDt-timestamp.jpg"
      // The nanoID occupies exactly the first NANO_ID_LENGTH characters of the filename.
      const nanoId = decoded.substring(0, NANO_ID_LENGTH);
      // Validate: must be exactly NANO_ID_LENGTH chars and match allowed character set.
      if (!NANO_ID_PATTERN.test(nanoId)) return null;
      return BaseAPI.buildProfilePictureURL(nanoId);
    } catch {
      return null;
    }
  }

  /**
   * Convenience: accepts either a nanoID or a signed S3 URL.
   * Prefers the public streaming URL either way.
   */
  static resolveProfilePictureURL(
    assetId: string | null | undefined,
    signedUrl: string | null | undefined
  ): string | null {
    return (
      BaseAPI.buildProfilePictureURL(assetId) ||
      BaseAPI.buildProfilePictureURLFromSignedURL(signedUrl) ||
      null
    );
  }

  /**
   * Core HTTP request method.
   *
   * All requests go through the shared `axiosInstance` which applies:
   *   • A 30 s default timeout (covers normal API latency + proxy hop)
   *   • A global error interceptor that shows user-facing toasts for network errors,
   *     403 Forbidden, and 5xx Server Errors while still re-throwing so per-call
   *     handlers can add their own handling.
   *
   * Per-call timeout override (e.g. report generation):
   *   Pass `{ timeout: 120_000 }` as the `extraConfig` argument:
   *
   *     BaseAPI.request('GET', '/reports/export', null, cb, 'application/json', params, true, { timeout: 120_000 })
   *
   *   Or via any static helper that forwards to request():
   *     // add an extraConfig param to your helper if needed
   */
  static async request(
    method: Method,
    apiPath: string,
    data: any = null,
    callback?: (response: any) => void,
    contentType: string = 'application/json',
    params?: any,
    useAuth: boolean = true,
    /** Optional per-request axios config overrides (e.g. { timeout: 120_000 }) */
    extraConfig?: Record<string, any>
  ) {
    try {
      let headers: any;

      if (data instanceof FormData) {
        headers = useAuth ? BaseAPI.getAuthHeaders('multipart/form-data') : {};
      } else {
        headers = useAuth ? BaseAPI.getAuthHeaders(contentType) : { 'Content-Type': contentType };
      }

      if (method === 'DELETE') {
        delete headers['Content-Type'];
      }

      const axiosConfig: any = {
        method,
        url: BaseAPI.buildAPIURL(apiPath),
        headers,
        params,
        // Spread per-request overrides last so they take precedence over defaults
        ...extraConfig,
      };

      if (method !== 'DELETE') {
        axiosConfig.data = data;
      }

      // Use the shared axiosInstance (not bare Axios) so the timeout default and
      // global error interceptor are applied automatically.
      const response = await axiosInstance(axiosConfig);

      if (callback) {
        callback(response);
      }
      return response;
    } catch (error: any) {
      if (error.response) {
        const errorResponse: any = {
          status: error.response.status,
          message: error.response.data?.message || error.response.data?.error || error.response.data?.reason || 'General server error',
          // Pass through the full error body so callers can read structured fields
          // (e.g. code, available, required, lopDays for 409 INSUFFICIENT_BALANCE).
          data: error.response.data,
        };
        if (callback) {
          callback(errorResponse);
        }
        return errorResponse;
      } else {
        return undefined;
      }
    }
  }

  static async login(formdata: any, callback: any) {
    return this.request('POST', 'auth/login', formdata, callback);
  }

  static async logout(callback: any) {
    return this.request('POST', 'auth/logout', callback);
  }

  static async validateToken() {
    return this.request('GET', 'auth/validate-token');
  }

  static async userDetails(formdata: any, callback: any) {
    return this.request('POST', 'auth/register', formdata, callback);
  }

  static async tickettoolDetails(formdata: any, callback: any) {
    return this.request('POST', '/tickettool', formdata, callback);
  }

  static async tickettoolBulkUpdate(formdata: any[], callback: any) {
    return this.request('POST', '/tickettool/bulkupdate', formdata, callback);
  }

  static async tickettoolList(filter: any, callback?: (response: any, filter?: any) => void) {
    return this.request('GET', '/tickettool', null, (response) => callback?.(response, filter), 'application/json', filter);
  }

  static async tickettoolStatusCount(filter: any, callback: any) {
    return this.request('GET', '/tickettool/statuscount', null, callback, null, filter);
  }

  static async tickettoolStatusGroupingCount(filter: any, callback: any) {
    return this.request('GET', '/tickettool/statuscount/groupby', null, callback, null, filter);
  }

  static async tickettoolListAll(filter: any, callback?: any, p0?: number[]) {
    return this.request('POST', '/tickettool/search', filter, callback);
  }

  static async tickettoolHistoryList(filter: any, callback?: any) {
    return this.request('POST', '/tickettool/history', filter, callback);
  }

  static async getallbiometricData(callback: any) {
    return this.request('GET', '/bio-metric-connection/getAllAttendanceDetails', null, callback);
  }

  static async getResignationsForSupervisor(supervisorPartyId: string) {
    return this.request('GET', `/resignation/supervisor/${supervisorPartyId}`);
  }

  static async getResignations(): Promise<any> {
    const response = await this.request('GET', '/resignation');
    return response ? response.data : response;
  }

  static async submitResignation(data: any, callback: any) {
    return this.request('POST', '/resignation', data, callback);
  }

  static async getExitInterview(resignationId: string, callback: any) {
    return this.request('GET', `/exit-interviews/resignation/${resignationId}`, null, callback);
  }

  static async getExitInterviewByResignationId(resignationId: string, callback: any) {
    return this.request('GET', `/exit-interviews/resignation/${resignationId}`, null, callback);
  }

  static async submitExitInterview(exitInterviewData: any, callback: any) {
    return this.request('POST', '/exit-interviews', exitInterviewData, callback);
  }

  static async updateExitInterview(id: string, exitInterviewData: any, callback: any) {
    return this.request('PATCH', `/exit-interviews/${id}`, exitInterviewData, callback);
  }

  static async getResignationList(data: any, callback: any) {
    return this.request('GET', '/resignation', null, callback);
  }

  static async getResignationByEmployeeID(empId: any, callback: any) {
    return this.request('GET', `/resignation/employee/${empId}`, null, callback);
  }

  static async getResignationHistory(id: string, callback: any) {
    return this.request('GET', `/resignation/history/${id}`, {}, callback);
  }
  static async updateResignation(id: string, updateData: any, callback: any) {
    return this.request('PUT', `/resignation/${id}`, updateData, callback);
  }
  // static async updateResignation(id, updateData, callback) {
  //   await Axios.put(BaseAPI.buildAPIURL(`/resignation/${id}`), updateData, {
  //     headers: {
  //       'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
  //       'Content-Type': 'application/json'
  //     }
  //   })
  //   .then((response) => {
  //     callback(response);
  //   })
  //   .catch((error) => {
  //     if (error.response) {
  //       callback({
  //         status: error.response.status,
  //         message: error.response.data?.error
  //           ? error.response.data?.error
  //           : "Failed to update resignation",
  //         response: error.response,
  //       });
  //     } else {
  //       callback({
  //         status: 500,
  //         message: "An error occurred while updating resignation",
  //       });
  //     }
  //   });
  // }

  static async getResignationById(id: string, callback: any) {
    return this.request('GET', `/resignation/${id}`, null, callback);
  }

  static async getAllProjectsWithoutPagination(callback: any) {
    return this.request('GET', `/project/all`, null, callback);
  }
  static async getAllProjectsList(callback: any) {
    return this.request('GET', '/project-details/projects/list', null, callback);
  }

  static async newRoles(formdata: any, callback: any) {
    return this.request('POST', '/access-operation', formdata, callback);
  }

  static async getUserRoleDatabyRoleID(id: number, callback: any) {
    return this.request('GET', `sub-category/${id}`, null, callback);
  }

  static async getRolesByID(id: string, callback: any) {
    return this.request('GET', `/roles/role/${id}`, null, callback);
  }

  static async getRolesIdOperation(id: string, callback: any) {
    return this.request('GET', `/access-operation/${id}/role/operation`, null, callback);
  }

  // ===== V1 PERMISSIONS API (NEW RBAC SYSTEM) =====
  static async getMyPermissionsV2(callback?: any) {
    return this.request('GET', 'v1/permissions/me', null, callback);
  }

  static async getPermissionCatalogV2(callback?: any) {
    return this.request('GET', 'v1/permission-catalog', null, callback);
  }

  static async getRolePermissionsV2(roleId: number, callback?: any) {
    return this.request('GET', `v1/roles/${roleId}/permissions`, null, callback);
  }

  static async updateRolePermissionsV2(roleId: number, entityOperationIds: number[], callback?: any) {
    return this.request('PUT', `v1/roles/${roleId}/permissions`, { entityOperationIds }, callback);
  }
  // ===== END V1 PERMISSIONS API =====

  // ===== AUTHZ OBSERVABILITY (Phase 1 "See.") =====
  // GET /access-review/explain?userId=|email=&resource=&action=|capability=
  static async explainAccess(params: Record<string, string | number>, callback?: any) {
    const queryParams = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => [k, String(v)]))
    ).toString();
    return this.request('GET', `/access-review/explain?${queryParams}`, null, callback);
  }

  // GET /access-review/view-as?userId=|email=  (tenant-admin only; backend enforces)
  static async viewAsPermissions(params: { userId?: number; email?: string }, callback?: any) {
    const queryParams = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => [k, String(v)]))
    ).toString();
    return this.request('GET', `/access-review/view-as?${queryParams}`, null, callback);
  }
  // ===== END AUTHZ OBSERVABILITY =====


  static async getTicketToolId(id: string) {
    const response = await this.request('GET', `/tickettool/${id}`);
    if (response?.status === HttpStatusCode.Ok) {
      return response?.data;
    }
    return false;
  }

  static async editTicketTool(id: string) {
    return this.request('PUT', `/tickettool/${id}`);
  }

  static async listCommentData(callback: any) {
    return this.request('GET', '/ticketcommentstatus', null, callback);
  }

  static async getCommentDataByTicketId(id: any, callback: any) {
    return this.request('GET', `/ticketcommentstatus/ticket/${id}`, null, callback);
  }

  static async deactivateTicketComment(id: string, callback: any) {
    return this.request('DELETE', `/ticketcommentstatus/${id}`, null, callback);
  }

  static async getUserDetails(callback: any) {
    return this.request('GET', `/users`, null, callback);
  }

  static async getPrivilegedUsers(callback?: any) {
    return this.request('GET', `/users/privileged`, null, callback);
  }

  static async getUserDetailsByEmail(email, callback: any) {
    const params = { email: email };
    return this.request('GET', `/users`, null, callback, null, params);
  }

  static async getUserDetailsById(id, callback: any) {
    const params = { id: id };
    return this.request('GET', `/users`, null, callback, null, params);
  }

  static async updateUserDetails(id: number, formData: any, callback: any) {
    return this.request('PUT', `/users/${id}/update`, formData, callback);
  }

  static async updateUserRoles(id: number, formData: any, callback: any) {
    return this.request('PUT', `/user-roles/${id}`, formData, callback);
  }

  static async updateToolkitDetails(id: number, formData: any, callback: any) {
    return this.request('PUT', `/tickettool/${id}`, formData, callback);
  }

  static async createTicketToolHistory(id: number, callback: any) {
    return this.request('PUT', `/tickettool/${id}/history`, callback);
  }

  static async getTicketToolHistoryById(id: number, callback: any) {
    return this.request('GET', '/tickettool/history/' + id, null, callback);
  }

  static async getTicketToolHistory(payload: any, callback: any) {
    const queryParams = new URLSearchParams(payload).toString();
    return this.request('GET', `/tickettool/history/all?${queryParams}`, null, callback);
  }

  static async addCommand(formData: any, callback: any) {
    return this.request('POST', '/ticketcommentstatus', formData, callback);
  }

  static async addCommandImg(formData: any, callback: any) {
    return this.request('POST', '/ticketcommentstatus/img', formData, callback);
  }

  static async ticketCommandMail(formData: any, callback: any) {
    return this.request('POST', '/ticketcommentstatus/sendmail', formData, callback);
  }

  static async deleteUser(id: number, callback: any) {
    return this.request('DELETE', `/delete/${id}`, null, callback);
  }

  static async deleteEmployee(ids: number[], callback: any) {
    try {
      // Use the first ID in the path for routing
      const id = ids[0];

      // Make sure we're sending the proper payload
      const response = await this.request('DELETE', `/employee/${id}`, { ids });

      callback?.({ status: 200, message: 'Employees deleted successfully' });
    } catch (error) {
      console.error('Error in deleteEmployee:', error);
      callback?.({
        status: error.status || 500,
        message: error.message || 'General server error'
      });
    }
  }

  // Add these methods to your existing BaseAPI class

  // Create Template
  static async createTemplate(templateData: any, callback: any) {
    return this.request('POST', '/offer-letters/templates', templateData, callback);
  }

  // Update Template
  static async updateTemplate(id: number, templateData: any, callback: any) {
    return this.request('PUT', `/offer-letters/templates/${id}`, templateData, callback);
  }

  // Delete Template
  static async deleteTemplate(id: number, callback: any) {
    return this.request('DELETE', `/offer-letters/templates/${id}`, {}, callback);
  }

  // Get Single Template
  static async getTemplateById(id: number, callback: any) {
    return this.request('GET', `/offer-letters/templates/${id}`, {}, callback);
  }

  // Get Templates by Type (content or salary)
  static async getTemplatesByType(type: 'content' | 'salary', callback: any) {
    return this.request('GET', `/offer-letters/templates/type/${type}`, {}, callback);
  }

  // static async deleteEmployee(ids: number[], callback: any) {
  //   try {
  //     // For consistency, use the first ID in the path but send all IDs in the body
  //     const firstId = ids[0];
  //     const response = await this.request("DELETE", `/employee/${firstId}`, { ids });
  //     callback?.({ status: 200, message: 'Employees deleted successfully' });
  //   } catch (error) {
  //     callback?.({
  //       status: error.status || 500,
  //       message: error.message || "General server error",
  //     });
  //   }
  // }

  // static async deleteEmployee(ids: number[], callback: any) {
  //   try {
  //     const promises = ids.map(id => this.request("DELETE", `/employee/${id}`));
  //     await Promise.all(promises);
  //     callback?.({ status: 200, message: 'Employees deleted successfully' });
  //   } catch (error) {
  //     callback?.({
  //       status: error.status || 500,
  //       message: error.message || "General server error",
  //     });
  //   }
  // }

  static async deleteTicketTool(id: number, callback: any) {
    return this.request('DELETE', `/tickettool/delete/${id}`, null, callback);
  }

  static async activateTicketTool(id: number, callback: any) {
    return this.request('DELETE', `/tickettool/restore/${id}`, null, callback);
  }

  static async getUserEditDetails(callback: any) {
    return this.request('GET', '/user_details', null, callback);
  }

  static async getEmployeeNameDetails(callback?: (response: any, filter?: any) => void, filter?: any) {
    return this.request('GET', '/employee-details', null, callback, filter);
  }

  static async getEmployeeData(callback: any) {
    return this.request('GET', '/employee-details', null, callback);
  }

  static async addRoles(formData: any, callback: any) {
    return this.request('POST', '/user-roles/addRoles', formData, callback);
  }

  static async getSubCategory(callback: any) {
    return this.request('GET', '/sub-category', null, callback);
  }

  static async getCategory(callback: any) {
    return this.request('GET', '/category', null, callback);
  }

  // Same shape as getCategory (category.subCategory[]), but scoped to the
  // current tenant's entitled modules — use this for screens that grant
  // permissions (e.g. the role editor), not getCategory's unfiltered catalog.
  static async getCategoryForTenant(callback: any) {
    return this.request('GET', '/sub-category/grouped', null, callback);
  }

  static async updateSubCategoryStatus(payload: any, id: number, callback: any) {
    return this.request('PUT', `/sub-category/status/${id}`, payload, callback);
  }

  static async updateCategory(payload: any, id: number, callback: any) {
    return this.request('PATCH', `/category/${id}`, payload, callback);
  }

  static async getRoles(callback: any) {
    return this.request('GET', 'roles/role-details', null, callback);
  }

  static async getRolesById(id, callback: any) {
    return this.request('GET', `roles/${id}`, null, callback);
  }

  static async getRolesData(callback: any) {
    return this.request('GET', '/user-roles', null, callback);
  }

  static async getPersonalInformationID(id: any, callback: any) {
    return this.request('GET', `/employee/${id}`, null, callback);
  }

  static async getEmployeeProfile(id: string, callback: any) {
    return this.request('GET', `/employee/profile/${id}`, null, callback);
  }

  static async getEmployeeByPartyID(id: number, callback: any) {
    return this.request('GET', `/employee/party/${id}`, null, callback);
  }

  /**
   * Lazily fetch a single employee's contact info (phone + email).
   * Rate-limited and audited server-side.
   * Returns { id, name, phone, email } on success.
   * Returns status 429 when the caller exceeds the rate limit.
   */
  static async getEmployeeContactInfo(
    employeeId: number,
    callback?: (response: any) => void
  ) {
    return this.request('GET', `/employee/${employeeId}/contact-info`, null, callback);
  }

  static async getAllPersonalInformationID(id: any, callback: any) {
    return this.request('GET', `/employee/all/${id}`, null, callback);
  }

  static async getAllEmployeeByPartyID(id: number, callback: any) {
    return this.request('GET', `/employee/all/party/${id}`, null, callback);
  }

  static async getPersonalInformation(callback: any) {
    return this.request('GET', '/employee', null, callback);
  }

  static async getAllPersonalInformation(callback: any, params?: any) {
    return this.request('GET', '/employee/all', null, callback, 'application/json', params);
  }

  static async getActiveEmployee(callback: any) {
    return this.request('GET', '/employee/active-employee', null, callback);
  }

  /**
   * Lightweight employee lookup for autocomplete/dropdowns
   * @param params - { active?: boolean, department?: string, limit?: number, fieldsets?: string }
   */
  static async getEmployeeLookup(params?: any, callback?: any) {
    // Served by the Go backend (flat rows with top-level partyId).
    return this.request('GET', '/employee/lookup', null, callback, 'application/json', params);
  }

  /**
   * Search employees for autocomplete (lightweight, server-side search).
   * Always served by the Go backend; callers rely on it to filter by `q`.
   * @param query - Search query string
   * @param params - { active?: boolean, limit?: number, fieldsets?: string }
   */
  static async searchEmployeeLookup(query: string, params?: any, callback?: any) {
    const searchParams = { q: query, ...params };
    return this.request('GET', '/employee/lookup/search', null, callback, 'application/json', searchParams);
  }

  static async addAttendenceUpload(formData: any, callback: any) {
    return this.request('POST', '/user-data', formData, callback, 'multipart/form-data');
  }
  static async assetBulkUpload(URL: any, formdata: any, callback: any) {
    return this.request('POST', `/${URL}/upload`, formdata, callback, 'multipart/form-data');
  }

  static async getAssetsByIds(assetIds: string[]) {
    const params = new URLSearchParams();
    assetIds.forEach((id) => params.append('ids[]', id));
    return this.request('GET', `/asset/bulkGet?${params.toString()}`, null, null);
  }

  static async getExpenseDetailByUserId(userId: any, expenseId: any, callback: any) {
    return this.request('GET', `/expenses/${userId}/${expenseId}`, null, callback);
  }

  static async addPersonalInformation(formData: any, callback: any) {
    return this.request('POST', '/employee', formData, callback);
  }

  static async deleteDocument(id: number, callback: any) {
    return this.request('DELETE', `/document/${id}`, null, callback);
  }

  static async updateDocument(id: number, formData: any, callback: any) {
    return this.request('PUT', `/document/${id}`, formData, callback);
  }

  static async addDocument(id: number, formData: any, callback: any) {
    return this.request('POST', '/document', formData, callback);
  }

  static async updatePersonalInformation(id: number, formData: any, callback: any) {
    return this.request('PUT', `/employee/${id}/update`, formData, callback);
  }

  static async addEmployeeUpload(formData: any, callback: any) {
    return this.request('POST', '/user-data', formData, callback, 'multipart/form-data');
  }

  static async getEmployeeDetails(callback?: (response: any, filter?: any) => void, filter?: any) {
    return this.request('GET', '/user-data', null, callback, null, filter);
  }

  static async getSettingsDetails(callback: any) {
    return this.request('GET', '/settings', null, callback);
  }

  static async getLogoAssetId(callback: any) {
    return this.request('GET', '/settings/asset-id', null, callback, 'application/json', null, false);
  }

  static async getDepartmentList(callback: any) {
    return this.request('GET', '/department', null, callback);
  }

  static async deleteDepartment(id: number, callback: any) {
    return this.request('DELETE', `/department/${id}`, null, callback);
  }

  static async deleteDesignation(id: number, callback: any) {
    return this.request('DELETE', `/designation/${id}`, null, callback);
  }

  static async deleteJobDetailsHistory(id: number, callback: any) {
    return this.request('DELETE', `/job-details-history/${id}`, null, callback);
  }

  static async getDesignationList(callback: any) {
    return this.request('GET', '/designation', null, callback);
  }

  static async getDesignationByID(id: number, callback: any) {
    return this.request('GET', `/designation/get/${id}`, null, callback);
  }

  static async getDepartmentByID(id: number, callback: any) {
    return this.request('GET', `/department/get/${id}`, null, callback);
  }

  static async deleteWorkLocation(id: number, callback: any) {
    return this.request('DELETE', `/work-locations/${id}`, null, callback);
  }

  static async getWorkLocationList(callback: any) {
    return this.request('GET', '/work-locations', null, callback);
  }

  static async getWorkLocationByID(id: number, callback: any) {
    return this.request('GET', `/work-locations/get/${id}`, null, callback);
  }

  static async settingDetails(formdata: any, callback: any) {
    return this.request('POST', '/settings', formdata, callback);
  }

  static async settingWorkLocationDetails(formdata: any, callback: any) {
    return this.request('POST', '/work-locations', formdata, callback);
  }

  static async updateWorkLocation(id: number, formdata: any, callback?: any) {
    return this.request('PUT', `/work-locations/${id}`, formdata, callback);
  }

  static async getWorkLocationSchedule(id: number, callback: any) {
    return this.request('GET', `work-locations/${id}/schedule`, null, callback);
  }

  static async updateWorkLocationSchedule(id: number, body: any, callback?: any) {
    return this.request('PUT', `work-locations/${id}/schedule`, body, callback);
  }

  static async settingDepartmentsDetails(formdata: any, callback: any) {
    return this.request('POST', '/department', formdata, callback);
  }

  static async settingDesignationDetails(formdata: any, callback: any) {
    return this.request('POST', '/designation', formdata, callback);
  }

  static async updateDesignationDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/designation/${id}`, formdata, callback);
  }

  static async updateDepartmentsDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/department/${id}`, formdata, callback);
  }
  static async updateAttendanceStatus(payload: any, callback: any) {
    return this.request('PUT', '/shift-coverage/attendance/update-status', payload, callback);
  }

  static async updateSettingsDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/settings/${id}`, formdata, callback);
  }

  static async createLeave(formdata: any, callback: any) {
    return this.request('POST', '/leaveRequest', formdata, callback);
  }

  static async leaveCreate(formdata: any, callback: any) {
    return this.request('POST', '/leave_transaction', formdata, (response) => {
      callback(response);
    });
  }

  static async getMailOptions(formdata: any, callback: any) {
    const queryParams = new URLSearchParams(formdata).toString();
    return this.request('GET', `/mailoptions?${queryParams}`, null, callback);
  }

  static async getMailOptionsById(id: any, callback: any) {
    return this.request('GET', `/mailoptions/${id}`, null, callback);
  }

  static async saveMailOption(formdata: any, callback: any) {
    return this.request('POST', `/mailoptions/`, formdata, callback);
  }

  static async updateMailOption(id: any, formdata: any, callback: any) {
    return this.request('PATCH', `/mailoptions/${id}`, formdata, callback);
  }

  static async getAllLeaveType(callback: any) {
    return this.request('GET', '/leave_type/leave', null, callback);
  }
  static async getLeaveData(callback: any) {
    return this.request('GET', '/leave_transaction', null, callback);
  }

  static async getLeaveDataByUser(userId: number, callback: any) {
    return this.request('GET', `/leave_transaction/user/${userId}`, null, callback);
  }

  static async searchMyLeaves(filters: any, callback: any) {
    return this.request('POST', '/leave_transaction/search-my-leaves', filters, callback);
  }

  static async searchTeamLeaves(filters: any, callback: any) {
    return this.request('POST', '/leave_transaction/search-team-leaves', filters, callback);
  }

  // static async getRequestedLeaveBalance(userId: number, leaveType: string, callback: any) {
  //   return this.request("GET", `/leave_transaction/requested-balance/user/${userId}/leaveType/${leaveType}`, null, callback);
  // }

  static async getLeaveBalances(callback: any) {
    return this.request('GET', '/leave-balance', null, callback);
  }

  static async searchMyLeaveBalance(filters: any, callback: any) {
    return this.request('POST', '/leave-balance/search-my-leave-balance', filters, callback);
  }

  static async searchTeamLeaveBalance(filters: any, callback: any) {
    return this.request('POST', '/leave-balance/search-team-leave-balance', filters, callback);
  }

  static async updateLeaveRequest(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/leave_transaction/${id}`, formdata, callback);
  }

  static async cancelIndividualDay(formdata: any, callback: any) {
    return this.request('POST', '/leave_transaction/cancel-individual-day', formdata, (response) => {
      callback(response);
    });
  }

  static async cancelIndividualDate(id: number, cancelData: any, callback: any) {
    return this.request('PATCH', `/leave_transaction/${id}/cancel-individual-date`, cancelData, callback);
  }

  static async approveIndividualCancellation(id: number, action: 'approve' | 'decline', data: any, callback: any) {
    return this.request('PATCH', `/leave_transaction/${id}/individual-cancellation/${action}`, data, callback);
  }

  static async getByIdLeaveData(id: string) {
    try {
      const response = await this.request('GET', `/leave_transaction/${id}`);
      if (response?.status === HttpStatusCode.Ok) {
        return response?.data;
      }
      return false;
    } catch (error) {
      return {
        status: error.response?.status || 500,
        message: error.response?.data?.error || 'General server error'
      };
    }
  }

  static async getByIdLeaveList(id: number, callback: any) {
    return this.request('GET', `/leave_transaction/${id}`, null, callback);
  }

  static async getByIdLeaveRequest(query: any, callback: any) {
    if (!Object.keys(query).length) return;

    const queryParams = new URLSearchParams(query).toString();
    return this.request('GET', `/leave_transaction/?${queryParams}`, null, callback);
  }

  static async createLeavetype(formdata: any, callback: any) {
    return this.request('POST', '/leave_type', formdata, callback);
  }

  static async getLeaveTypeListData(callback: any) {
    return this.request('GET', '/leave_type/all', null, callback);
  }

  static async deleteLeaveType(id: number, callback: any) {
    return this.request('DELETE', `/leave_type/delete/${id}`, null, callback);
  }

  static async updateLeaveTypeDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/leave_type/${id}`, formdata, callback);
  }

  static async createCompOff(formdata: any, callback: any) {
    return this.request('POST', '/comp_off', formdata, callback);
  }
  // Get all comp off records
  static async getAllCompOffs(callback: any) {
    return this.request('GET', '/comp_off', null, callback);
  }

  // Get comp off by ID
  static async getCompOffById(id: number, callback: any) {
    return this.request('GET', `/comp_off/${id}`, null, callback);
  }

  // Get comp offs for a specific user
  static async getCompOffsByUser(partyId: number, callback: any) {
    return this.request('GET', `/comp_off/user/${partyId}`, null, callback);
  }

  // Update comp off
  static async updateCompOff(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/comp_off/${id}`, formdata, callback);
  }

  // Update request status
  static async updateCompOffStatus(id: number, status: number, callback: any) {
    return this.request('PATCH', `/comp_off/${id}/request-status`, { status }, callback);
  }

  // Delete comp off
  static async deleteCompOff(id: number, callback: any) {
    return this.request('DELETE', `/comp_off/${id}`, null, callback);
  }

  static async getAllOfferLetterTemplates(callback: any) {
    return this.request('GET', `/offer-letters/templates`, null, callback);
  }

  // Get content templates
  static async getContentTemplates(callback: any) {
    return this.request('GET', `/offer-letters/templates/type/content`, null, callback);
  }

  // Get salary templates
  static async getSalaryTemplates(callback: any) {
    return this.request('GET', `/offer-letters/templates/type/salary`, null, callback);
  }

  // Create offer letter
  static async createOfferLetter(formdata: any, callback: any) {
    return this.request('POST', `/offer-letters`, formdata, callback);
  }

  // Generate preview (without saving)
  static async generateOfferLetterPreview(formdata: any, callback: any) {
    return this.request('POST', `/offer-letters/preview`, formdata, callback);
  }

  // Get all offer letters
  static async getAllOfferLetters(callback: any) {
    return this.request('GET', `/offer-letters`, null, callback);
  }

  // Get offer letter by ID
  static async getOfferLetterById(id: number, callback: any) {
    return this.request('GET', `/offer-letters/${id}`, null, callback);
  }

  // Get ACTIVE content templates only (for Offer Letter Generator)
  static async getActiveContentTemplates(callback: any) {
    return this.request('GET', `/offer-letters/templates/type/content/active`, null, callback);
  }

  // Get ACTIVE salary templates only (for Offer Letter Generator)
  static async getActiveSalaryTemplates(callback: any) {
    return this.request('GET', `/offer-letters/templates/type/salary/active`, null, callback);
  }
  // Update offer letter by ID
  static async updateOfferLetter(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/offer-letters/${id}`, formdata, callback);
  }

  // Delete offer letter by ID
  static async deleteOfferLetter(id: number, callback: any) {
    return this.request('DELETE', `/offer-letters/${id}`, null, callback);
  }
  static async getPartyDetails(callback: any) {
    return this.request('GET', '/users', null, callback);
  }
  static async getPartyDetailsByEmail(email, callback: any) {
    const param = { email: email };
    return this.request('GET', '/users', null, callback, null, param);
  }
  static async getPartyDetailsById(id, callback: any) {
    const param = { id: id };
    return this.request('GET', '/users', null, callback, null, param);
  }
  static async getAllStatusUpdate(callback?: any) {
    return this.request('GET', '/status-updates', null, callback);
  }
  static async getAllStatusUpdateNew(formdata, callback?: any) {
    return this.request('POST', '/status-updates/search', formdata, callback);
  }
  static async getStatusUpdateById(id: string, callback?: any) {
    return this.request('GET', `/status-updates/${id}`, null, callback);
  }

  static async getStatusUpdateByTicketId(id: string, callback?: any) {
    return this.request('GET', `/status-updates/ticketid/${id}`, null, callback);
  }

  static async submitContactForm(formdata: any, callback: any) {
    return this.request('POST', '/contact-us', formdata, callback);
  }

  static async EmployeeNameData(query: any, callback: any) {
    if (!Object.keys(query).length) return;

    const queryParams = new URLSearchParams(query).toString();
    return this.request('GET', `/employee-details?${queryParams}`, null, callback);
  }

  static async UserData(query: any, callback: any) {
    if (!Object.keys(query).length) return;

    const queryParams = new URLSearchParams(query).toString();
    return this.request('GET', `/user-data?${queryParams}`, null, callback);
  }

  static async getUserList(callback?: any, params?: { pendingInvite?: boolean }) {
    return this.request('GET', '/users', null, callback, 'application/json', params);
  }

  /**
   * Re-mints and re-sends the invite (set-password) email for one or many
   * existing users. A single resend is just a batch of one — there is no
   * separate single-user endpoint, so callers always pass an array.
   * Always resolves 200 with a per-user { sent, skipped, failed } breakdown
   * unless the request itself is malformed (empty list, over the batch cap).
   */
  static async resendInvite(userIds: number[], callback?: any) {
    return this.request('POST', '/v1/users/resend-invite', { userIds }, callback);
  }

  static async getByIdUserList(id: number, callback: any) {
    return this.request('GET', `/users/${id}`, null, callback);
  }

  static async getProfileImage(id: number, callback: any) {
    return this.request('GET', `/asset/${id}`, null, callback);
  }

  static async fetchUserProfileImage(userId: number) {
    try {
      const response = await this.request('GET', `/api/assets/user/${userId}/profile-image`);
      return response.data.data; // Contains { id, url, title, contentType, uploadedStatus }
    } catch (error) {
      console.error('Error fetching profile image:', error);
      return null;
    }
  }

  static async getAllDocuments(callback: any) {
    return this.request('GET', `/document`, null, callback);
  }

  static async getDocumentById(id: number, callback: any) {
    return this.request('GET', `/document/${id}`, null, callback);
  }
  static async getAssetsWithPagination(page: number, limit: number, filters: any, callback: any) {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...filters
    }).toString();

    return this.request('GET', `/asset/paginated?${queryParams}`, null, callback);
  }

  static async updateUserInformation(id: number, formdata: any, callback: any) {
    return this.request('PUT', `users/${id}/update`, formdata, callback);
  }

  static async getEmployeeListFilter(callback: any, filters?: any) {
    const queryParams = filters && Object.keys(filters).length ? new URLSearchParams(filters).toString() : '';
    return this.request('GET', `/users${queryParams ? `?${queryParams}` : ''}`, null, callback);
  }

  static async getEmployeeFilter(callback: any, filter?: any) {
    const queryParams = filter && Object.keys(filter).length ? new URLSearchParams(filter).toString() : '';
    return this.request('GET', `/users${queryParams ? `?${queryParams}` : ''}`, null, callback);
  }

  static async getEmployeeFilterr(callback: any) {
    return this.request('GET', '/users', null, callback);
  }

  static async getCodeReference(callback?: (response: any) => void) {
    return this.request('GET', '/code-table', null, callback);
  }

  static async getEmployeeRole(callback: any) {
    return this.request('GET', '/roles/role-details', null, callback);
  }

  static async getRequestLeaveBalances(userId: number, leaveType: string, callback: any) {
    return this.request('GET', `/leave_transaction/user/${userId}/leaveType/${leaveType}`, null, callback);
  }

  static async getUserLeaveBalancesByLeaveType(userId: number, leaveType: string, callback: any) {
    return this.request('GET', `/leave-balance/user/${userId}/leaveType/${leaveType}`, null, callback);
  }
  static async updateRequestedLeave(employeeId: number, leaveTypeId: number, days: number, isAdd: boolean, callback: any) {
    return this.request('POST', `/leave-balance/update-requested-leave/${employeeId}/leaveType/${leaveTypeId}`, { days, isAdd }, callback);
  }

  // Inside your BaseAPI class

  static async createLeaveAdjustment(payload: any, callback: any) {
    return this.request('POST', `/leave-adjustments`, payload, callback);
  }

  static async getAllLeaveAdjustments(callback: any) {
    return this.request('GET', '/leave-adjustments', null, callback);
  }
  static async getRequestedLeaveBalance(userId: number, leaveTypeId: number, callback: any) {
    return this.request('GET', `/leave-balance/requested-leave/${userId}/leaveType/${leaveTypeId}`, null, callback);
  }

  static async updateRequestedLeaveBalance(employeeId: number, leaveTypeId: number, days: number, isAdd: boolean, callback: any) {
    const data = { days, isAdd };
    return this.request('PATCH', `/leave-balance/update-requested-leave/${employeeId}/leaveType/${leaveTypeId}`, data, callback);
  }

  static async createIndividualCancelRequest(formdata: any, callback: any) {
    return this.request('POST', '/leave_individual_cancel_request', formdata, callback);
  }
  static async updateIndividualLeaveDate(leaveId: number, formdata: any, callback: any) {
    return this.request('PUT', `/leave_transaction/${leaveId}/individual_date`, formdata, callback);
  }

  static async updateLeaveBalanceOnStatusChange(employeeId: number, leaveTypeId: number, updateData, callback: any) {
    return this.request('PUT', `/leave-balance/update-on-status-change/${employeeId}/leaveType/${leaveTypeId}`, updateData, callback);
  }
  // static async updateLeaveBalanceOnStatusChange(employeeId: number, leaveTypeId: number, days: number, status: string, callback: any) {
  //   const data = { days, status };
  //   return this.request("PATCH", `/leave-balance/update-on-status-change/${employeeId}/leaveType/${leaveTypeId}`, data, callback);
  // }

  static async updateLeaveBalanceById(
    id: number,
    updateData: { leaveTypeId: number; leaveDeducted: number },
    callback: (result: any) => void
  ) {
    try {
      const result = await this.request('PATCH', `/leave-balance/${id}`, updateData); // assuming this.request returns a Promise
      callback(result); // Call the callback with the result
    } catch (error) {
      callback({ status: 500, message: 'An error occurred' });
    }
  }

  static async getUserLeaveBalances(userId: any, callback: any) {
    return this.request('GET', `/leave-balance/user/${userId}`, null, callback);
  }

  static async getCategoryFind(currentPath: string, callback: any) {
    const formattedPath = currentPath.charAt(0).toUpperCase() + currentPath.slice(1);
    return this.request('GET', `/sub-category/role/operation?category=${formattedPath}`, null, callback);
  }

  static async addCodeReference(formData: any, callback: any) {
    return this.request('POST', '/code-table', formData, callback);
  }

  static async updateCodeReference(formdata: any, callback: any) {
    return this.request('PUT', `/code-table`, formdata, callback);
  }

  static async forgotPassword(formdata: any, callback: any) {
    return this.request('POST', '/auth/forget-password', formdata, callback, 'application/json', null, false);
  }
  static async resetPassword(formdata: any, callback: any) {
    return this.request('PUT', '/auth/reset-password', formdata, callback, 'application/json', null, false);
  }
  // Client related API Calls

  static async getClientData(callback: any, filter?: any) {
    const queryParams = filter ? new URLSearchParams(filter).toString() : '';
    return this.request('GET', `/clients${queryParams ? `?${queryParams}` : ''}`, null, callback);
  }

  static async getClientsLookup(callback: any) {
    return this.request('GET', `/v1/clients/lookup`, null, callback);
  }

  static async getClientDataById(id, callback: any) {
    return this.request('GET', `/clients/${id}`, null, callback);
  }

  static async createClientData(formdata: any, callback: any) {
    return this.request('POST', `/clients`, formdata, callback);
  }

  static async updateClientDataById(formdata: any, id, callback: any) {
    return this.request('PUT', `/clients/${id}`, formdata, callback);
  }

  static async updateContactPersonById(formdata: any, id, callback: any) {
    return this.request('PUT', `/clients/${id}/contactperson`, formdata, callback);
  }

  static async allProject(callback: any) {
    return this.request('GET', `/project`, null, callback);
  }

  static async deleteProjects(id: number, callback: any) {
    return this.request('DELETE', `/project/${id}`, undefined, callback);
  }

  static async deleteTickets(id: string, callback: any) {
    return this.request('DELETE', `/tickets/${id}`, null, callback);
  }

  static async ticketList(filter: any, callback: any) {
    const queryParams = new URLSearchParams(filter).toString();
    return this.request('GET', `/tickets?${queryParams}`, null, callback);
  }

  static async addComment(formdata: any, callback: any) {
    return this.request('POST', '/status', formdata, callback, 'multipart/form-data');
  }

  static async getCommentList(callback: any) {
    return this.request('GET', '/status', null, callback);
  }

  static async getTicketByID(id: string, callback: any) {
    return this.request('GET', `/tickets/${id}`, null, callback);
  }

  static async userList(filter: any, callback: any) {
    const queryParams = new URLSearchParams(filter).toString();
    return this.request('GET', `/users${queryParams ? `?${queryParams}` : ''}`, null, callback);
  }

  static async addUserList(formdata: any, callback: any) {
    return this.request('POST', '/users', formdata, callback);
  }

  static async getUserByID(id: number, callback: any) {
    return this.request('GET', `/users/${id}`, null, callback);
  }

  static async updateUserByID(payload: any, id: number, callback: any) {
    return this.request('PUT', `/users/${id}/update`, payload, callback);
  }

  static async updateEmployeeByID(payload: any, id: number, callback: any) {
    return this.request('PUT', `/employee/${id}/update`, payload, callback);
  }

  static async updateRoles(id: number, payload: any, callback: any) {
    return this.request('PUT', `access-operation/${id}/role-operation`, payload, callback);
  }

  static async addProject(formdata: any, callback: any) {
    return this.request('POST', '/project', formdata, callback);
  }

  static async getProjectByID(id: number, callback: any) {
    return this.request('GET', `/project/${id}`, null, callback);
  }

  static async getProjectByClientID(id: number, callback: any) {
    return this.request('GET', `/project/client/${id}`, null, callback);
  }

  static async updateProjectByID(formdata: any, id: number, callback: any) {
    return this.request('PUT', `/project/${id}`, formdata, callback);
  }

  static async setProjectPrefix(id: number, prefix: string | null) {
    return this.request('PATCH', `/project/${id}/prefix`, { prefix: prefix || null }, null, 'application/json');
  }

  static async updateTicketbyAdmin(formdata: any, id: number, callback: any) {
    return this.request('PUT', `/tickets/${id}`, formdata, callback, 'multipart/form-data');
  }

  static async updateTicketbyUser(payload: any, id: number, callback: any) {
    return this.request('PATCH', `/tickets/${id}/user`, payload, callback);
  }

  static async addTicket(formdata: any, Userinfo: any, callback: any) {
    return this.request('POST', '/tickets', formdata, callback, 'multipart/form-data');
  }

  static async allUsers(callback: any) {
    return this.request('GET', '/users/all', null, callback);
  }

  static async tickets(filter: any = null, callback: any) {
    const queryParams = new URLSearchParams(filter).toString();
    return this.request('GET', `/tickets${queryParams ? `?${queryParams}` : ''}`, null, callback);
  }

  static async projectList(filter: any, callback: any) {
    const queryParams = new URLSearchParams(filter).toString();
    return this.request('GET', `/project${queryParams ? `?${queryParams}` : ''}`, null, callback);
  }

  static async addJobDetails(id: number, formdata: any, callback: any) {
    return this.request('POST', `/job-details/${id}`, formdata, callback);
  }

  static async getJobDetails(id: string, callback: any) {
    return this.request('GET', `/job-details/get-one/${id}`, null, callback);
  }

  /**

   * @deprecated No caller since MyDetailsInfo/ was removed (2026-08-24).

   *

   * Backed by GET /address/get/{id}, which BOTH backends still serve — this marker is a

   * client-side signal only, NOT a statement that the endpoint is dead.

   * Do not delete either side without checking non-React consumers:

   * the mobile app was checked (no references), but integrations and

   * direct API users were not.

   */

  static async getCommAddressDetails(id: string, callback: any) {
    return this.request('GET', `/address/get/${id}`, null, callback);
  }

  static async getProjectUserByDetails(projectName: string, callback: any) {
    return this.request('GET', `/project-details/user/${projectName}`, null, callback);
  }

  // Get projects for dropdown (active projects only)
  static async getProjectsForDropdown(callback: any) {
    return this.request('GET', '/project-details/dropdown/projects-list', null, callback);
  }

  // Get all projects with pagination
  static async getAllProjects(page: number = 1, limit: number = 100, callback: any) {
    return this.request('GET', `/project?offset=${page}&limit=${limit}`, null, callback);
  }

  static async createDefectCommandWithFilesV2(formData: FormData, callback: any) {
    return this.request('POST', '/defect-tracker-commands/with-files', formData, callback);
  }

  // This should already exist - just verify it's there
  static async getProjectDetails(id: string, callback: any) {
    return this.request('GET', `/project-details/${id}`, null, callback);
  }

  /**

   * @deprecated No caller since MyDetailsInfo/ was removed (2026-08-24).

   *

   * Backed by GET /educational/{id}, which BOTH backends still serve — this marker is a

   * client-side signal only, NOT a statement that the endpoint is dead.

   * Do not delete either side without checking non-React consumers:

   * the mobile app was checked (no references), but integrations and

   * direct API users were not.

   */

  static async getEducationalDetails(id: string, callback: any) {
    return this.request('GET', `/educational/${id}`, null, callback);
  }

  /**

   * @deprecated No caller since MyDetailsInfo/ was removed (2026-08-24).

   *

   * Backed by GET /technical-skills/{id}, which BOTH backends still serve — this marker is a

   * client-side signal only, NOT a statement that the endpoint is dead.

   * Do not delete either side without checking non-React consumers:

   * the mobile app was checked (no references), but integrations and

   * direct API users were not.

   */

  static async getTechnicalSkillsDetails(id: string, callback: any) {
    return this.request('GET', `/technical-skills/${id}`, null, callback);
  }

  static async getDistinctSkills(callback: any) {
    return this.request('GET', '/technical-skills/distinct', null, callback);
  }

  /**

   * @deprecated No caller since MyDetailsInfo/ was removed (2026-08-24).

   *

   * Backed by GET /professional-details/{id}, which BOTH backends still serve — this marker is a

   * client-side signal only, NOT a statement that the endpoint is dead.

   * Do not delete either side without checking non-React consumers:

   * the mobile app was checked (no references), but integrations and

   * direct API users were not.

   */

  static async getProfessionalDetails(id: string, callback: any) {
    return this.request('GET', `/professional-details/${id}`, null, callback);
  }

  static async updateJobDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/job-details/${id}`, formdata, callback);
  }

  static async createJobDetailsHistory(id: number, formdata: any, callback: any) {
    return this.request('POST', `/job-details-history/${id}`, formdata, callback);
  }

  static async getJobdetailsHistory(id: string, callback: any) {
    return this.request('GET', `job-details-history/get-one/${id}`, null, callback);
  }

  static async getNameUnderImmediateSupervisor(immediatesupervisor: string, callback: any) {
    return this.request('GET', `job-details/${immediatesupervisor}`, null, callback);
  }

  static async getEmployeeIdsBySupervisorPartyId(partyId: number, callback: any) {
    return this.request('GET', `job-details/by-supervisor/${partyId}`, null, callback);
  }

  static async updateCommAddressDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/address/${id}`, formdata, callback);
  }

  static async updateEducationalDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/educational/${id}`, formdata, callback);
  }

  static async updateTechnicalSkillsDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/technical-skills/${id}`, formdata, callback);
  }

  static async updateProfessionalDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/professional-details/${id}`, formdata, callback);
  }

  static async updateProjectDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/project-details/${id}`, formdata, callback);
  }

  static async update(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/project-details/${id}`, formdata, callback);
  }

  static async getJobDesignation(id: string, callback: any) {
    return this.request('GET', `/job-details-history/${id}/designation`, null, callback);
  }

  static async getJobDepartment(id: string, callback: any) {
    return this.request('GET', `/job-details-history/${id}/department`, null, callback);
  }

  static async getJobPeoplemanager(id: string, callback: any) {
    return this.request('GET', `/job-details-history/${id}/peoplemanager`, null, callback);
  }

  static async getJobImmediatesupervisorHistory(id: string, callback: any) {
    return this.request('GET', `/job-details-history/${id}/immediatesupervisor`, null, callback);
  }

  static async getJobImmediatesupervisor(id: string, callback: any) {
    return this.request('GET', `/job-details/${id}/immediatesupervisor`, null, callback);
  }

  static async getHRDetailsData(id: string, callback: any) {
    return this.request('GET', `/HR-details/${id}`, null, callback);
  }

  static async updateHRDetailsData(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/HR-details/${id}/update`, formdata, callback);
  }

  static async updateImmediateSupervisorHistory(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/job-details-history/${id}/immediatesupervisor`, formdata, callback);
  }

  static async updatepeopleManagerHistory(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/job-details-history/${id}/peoplemanager`, formdata, callback);
  }

  static async updatedesignationHistory(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/job-details-history/${id}/designation`, formdata, callback);
  }

  static async updatedepartmentHistory(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/job-details-history/${id}/department`, formdata, callback);
  }

  static async getEmployeeAttendanceData(searchFilters: SearchQueryFilters, callback: any) {
    const filterAttrs = ['limit', 'offset', 'employeeId', 'query', 'employeeName', 'startDate', 'endDate', 'room', 'workType', 'scope', 'accessType'];
    const params = filterAttrs.reduce(
      (acc, attr) => {
        if (searchFilters[attr] !== undefined && searchFilters[attr] !== null && searchFilters[attr] !== '') {
          acc[attr] = searchFilters[attr];
        }
        return acc;
      },
      {} as Record<string, any>
    );

    const queryString = new URLSearchParams(params).toString();
    return this.request('GET', `bio-metric-connection${queryString ? `?${queryString}` : ''}`, null, callback);
  }

  static async addManualAttendance(entry: { employeeId: string; date: string; time: string; direction: 'in' | 'out'; accessType: 'fingerprint' | 'card'; room?: string }, callback: any) {
    return this.request('POST', '/bio-metric-connection/manual', entry, callback);
  }

  static async attendenceUpload(formdata: any, callback: any, state: string) {
    return this.request('POST', `/bio-metric-connection/upload/?state=${state}`, formdata, callback, 'multipart/form-data');
  }
  static async connectToBiometricDevice(state: string, selectedIp: string) {
    const apiPath = selectedIp
      ? `/bio-metric-connection/attendances?state=${state}&ip=${selectedIp}`
      : `/bio-metric-connection/attendances?state=${state}`;

    return this.request('GET', apiPath);
  }

  static async biolastentry(formdata: any, callback: any) {
    return this.request('POST', '/biometriclastsyncentry', formdata, callback, 'multipart/form-data');
  }

  static async getTicketFilter(filter: any, callback: any) {
    return this.request('GET', '/tickets/ticket/data', null, callback, 'application/json', filter);
  }

  static async createUploadData(uploadData: any, file: File, currentTab: string, callback: any) {
    // Initialize uploadData if undefined (Issue #15 fix)
    if (!uploadData) {
      uploadData = {};
    }
    uploadData.uploadFolder = currentTab;

    currentTab = currentTab.substring(0, currentTab.length - 1);
    const path = `/asset`;

    try {
      const result = await this.request('POST', path, uploadData);
      if (!result.data?.signedUrl || !result.data?.assetId) {
        throw new Error('Failed to get signed URL');
      }

      await Axios.put(result.data.signedUrl, file, {
        headers: {
          'Content-Type': file.type
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        }
      });

      const uploadStatus = await this.request(
        'GET',
        `/asset/${result.data.assetId}/get-signed-url`,
        null,
        null,
        'application/json',
        null,
        true
      );
      callback(uploadStatus);
    } catch (error) {
      console.error('Error in createUploadData:', error);
      callback(error);
    }
  }

  static async getAllAssets(callback: any) {
    try {
      const result = await this.request('GET', '/asset');
      callback(result);
    } catch (error) {
      console.error('Error fetching assets:', error);
      callback(error);
    }
  }
  static async getAllAssetsDataOnly(callback: any) {
    try {
      const result = await this.request('GET', '/asset/data-only');
      callback(result);
    } catch (error) {
      console.error('Error fetching assets:', error);
      callback(error);
    }
  }

  // static async createUploadDataBase64(uploadData: any, file: File | string, currentTab: string, callback: any) {
  //   uploadData.uploadFolder = currentTab;
  //   currentTab = currentTab.substring(0, currentTab.length - 1);
  //   const path = `/asset`;

  //   try {

  //     if (typeof file === 'string' && file.startsWith('data:')) {
  //       // Base64 upload
  //       uploadData.base64Data = file;
  //       uploadData.contentType = file.split(';')[0].split(':')[1];
  //       uploadData.title = `base64_upload_${Date.now()}`;
  //     } else if (file instanceof File) {
  //       // Existing file upload logic
  //       uploadData.title = file.name;
  //       uploadData.contentType = file.type;
  //     } else {
  //       throw new Error("Invalid file type");
  //     }
  //     const result = await this.request("POST", path, uploadData);

  //     if (!result.data?.signedUrl || !result.data?.assetId) {
  //       throw new Error("Failed to get signed URL");
  //     }

  //     if (file instanceof File && result.data?.signedUrl) {
  //       await Axios.put(result.data.signedUrl, file, {
  //         headers: {
  //           'Content-Type': file.type,
  //         },
  //         onUploadProgress: (progressEvent) => {
  //           const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
  //         },
  //       });
  //     }
  //     const uploadStatus = await this.request("GET", `/asset/${result.data.assetId}/get-signed-url`, null, null, "application/json", null, false);
  //     callback(uploadStatus);
  //   } catch (error) {
  //       console.error("Error in createUploadData:", error);
  //       callback(error);
  //   }
  // }

  static async createLogoUploadData(uploadData: any, file: File, currentTab: string, callback: any) {
    // Initialize uploadData if undefined (Issue #15 fix)
    if (!uploadData) {
      uploadData = {};
    }
    uploadData.uploadFolder = currentTab;
    currentTab = currentTab.substring(0, currentTab.length - 1);
    const path = `/asset`;

    try {
      const result = await this.request('POST', path, uploadData);
      if (!result.data?.signedUrl || !result.data?.assetId) {
        throw new Error('Failed to get signed URL');
      }

      await Axios.put(result.data.signedUrl, file, {
        headers: {
          'Content-Type': file.type
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        }
      });

      const uploadStatus = await this.request(
        'GET',
        `/asset/${result.data.assetId}/get-signed-url`,
        null,
        null,
        'application/json',
        null,
        false
      );
      callback(uploadStatus);
    } catch (error) {
      console.error('Error in createLogoUploadData:', error);
      callback(error);
    }
  }

  static async createStatusUpdate(data: any): Promise<any> {
    return this.request('POST', '/status-updates', data);
  }

  static async updateStatusUpdate(id: string, data: any, callback?: any) {
    return this.request('PUT', `/status-updates/${id}`, data, callback);
  }
  static async getAssetSignedUrl(assetId: string): Promise<IAsset | null> {
    const result = await this.request('GET', `/asset/${assetId}/get-signed-url`, null, null, 'application/json', null, true);
    return result.data?.asset || null;
  }

  static async getAssetSignedUrlWithCallBack(assetId: string, callback: any): Promise<IAsset | null> {
    const result = await this.request('GET', `/asset/${assetId}/get-signed-url`, null, callback, 'application/json', null, true);
    return result.data?.asset || null;
  }

  static async getPartyData(callback: any) {
    return this.request('GET', '/party', null, callback);
  }

  static async getAllAssetDetails(callback: any) {
    return this.request('GET', '/asset-details', null, callback);
  }

  static async getAssetDetailsPaginated(params: {
    page?: number;
    limit?: number;
    search?: string;
    assetType?: string;
    assetStatus?: string;
    // Server-side filters (all optional). List filters are sent as repeated
    // params (?assetType=A&assetType=B) and match case-insensitively.
    assetTypes?: string[];
    assetStatuses?: string[];
    assetIds?: string[];
    locations?: string[];
    insured?: string[]; // declared insured_status: yes | no | not_set
    coverage?: string; // derived coverage: covered | not_covered (asset insurance)
    make?: string;
    processor?: string;
    purchaseDate?: string; // YYYY-MM-DD
  }) {
    const query = new URLSearchParams();
    query.set('page', String(params.page ?? 1));
    query.set('limit', String(params.limit ?? 20));
    if (params.search) query.set('search', params.search);
    if (params.assetType) query.append('assetType', params.assetType);
    if (params.assetStatus) query.append('assetStatus', params.assetStatus);
    const appendAll = (key: string, values?: string[]) => (values || []).forEach((v) => v && query.append(key, v));
    appendAll('assetType', params.assetTypes);
    appendAll('assetStatus', params.assetStatuses);
    appendAll('assetId', params.assetIds);
    appendAll('location', params.locations);
    appendAll('insured', params.insured);
    if (params.coverage) query.set('coverage', params.coverage);
    if (params.make) query.set('make', params.make);
    if (params.processor) query.set('processor', params.processor);
    if (params.purchaseDate) query.set('purchaseDate', params.purchaseDate);
    return this.request('GET', `/asset-details?${query.toString()}`);
  }

  static async getAssetDetailsById(id: any) {
    const response = await this.request('GET', `/asset-details/${id}`);
    if (response?.status === 200 || response?.status === 201) {
      return response.data;
    }
    return false;
  }

  static async createAssetDetails(formdata: any) {
    return this.request('POST', '/asset-details', formdata);
  }

  static async updateAssetDetails(id: any, formdata: any) {
    return this.request('PUT', `/asset-details/${id}`, formdata);
  }

  static async deleteAssetDetails(id: any) {
    return this.request('DELETE', `/asset-details/${id}`);
  }

  static async getAssetProfile(assetId: string) {
    return this.request('GET', `/asset-details/profile/${assetId}`);
  }

  // Asset Linking
  static async getLinkedAssets(assetId: number) {
    return this.request('GET', `/asset-details/${assetId}/links`);
  }

  static async linkAsset(parentAssetId: number, payload: { childAssetId: number; notes?: string }) {
    return this.request('POST', `/asset-details/${parentAssetId}/links`, payload);
  }

  static async unlinkAsset(assetId: number, linkId: number) {
    return this.request('DELETE', `/asset-details/${assetId}/links/${linkId}`);
  }

  static async updateAssetCategory(assetId: number, payload: { category: string }) {
    return this.request('PATCH', `/asset-details/${assetId}/category`, payload);
  }

  static async searchAssets(query: string) {
    return this.request('GET', `/asset-details/search?q=${encodeURIComponent(query)}`);
  }

  static async createAssetUtilize(formdata: any) {
    return this.request('POST', '/asset-utilize', formdata);
  }

  static async getAllAssetUtilize(callback: any) {
    return this.request('GET', '/asset-utilize', null, callback);
  }

  // The Utilization list's server-side page (plan §6.5): sent only with `page`,
  // so the legacy full-array consumers above are unchanged.
  static async getAssetUtilizesPaginated(params: {
    page?: number;
    limit?: number;
    search?: string;
    statuses?: string[];
    assetTypes?: string[];
    assetIds?: string[];
    employeePartyId?: number;
    assignedFrom?: string; // YYYY-MM-DD, on the typed assigned_on column
    assignedTo?: string;
    make?: string;
    open?: 'true' | 'false';
  }) {
    const query = new URLSearchParams();
    query.set('page', String(params.page ?? 1));
    query.set('limit', String(params.limit ?? 20));
    if (params.search) query.set('search', params.search);
    (params.statuses || []).forEach((v) => v && query.append('status', v));
    (params.assetTypes || []).forEach((v) => v && query.append('assetType', v));
    (params.assetIds || []).forEach((v) => v && query.append('assetId', v));
    if (params.employeePartyId) query.set('employeePartyId', String(params.employeePartyId));
    if (params.assignedFrom) query.set('assignedFrom', params.assignedFrom);
    if (params.assignedTo) query.set('assignedTo', params.assignedTo);
    if (params.make) query.set('make', params.make);
    if (params.open) query.set('open', params.open);
    return this.request('GET', `/asset-utilize?${query.toString()}`);
  }

  // The Utilization list's OWN dropdown options (review fix 3): asset types and
  // asset ids derived from the allocation dataset, so utilization-only staff
  // and historical-only types are covered. A failure leaves the caller's empty
  // options in place (typing a value still works).
  static async getAssetUtilizeFilterOptions() {
    const response = await this.request('GET', '/asset-utilize/filter-options');
    throwIfError(response);
    if (response?.status === 200 && response.data) {
      return {
        assetTypes: Array.isArray(response.data.assetTypes) ? response.data.assetTypes : [],
        assetIds: Array.isArray(response.data.assetIds) ? response.data.assetIds : [],
      };
    }
    return { assetTypes: [], assetIds: [] };
  }

  // Dropdown options for the asset lists (plan §6.5, D15): one spelling per
  // case-insensitive group, chosen by the server. A failure leaves the caller's
  // empty options in place (typing a value still works).
  static async getAssetDetailsFilterOptions() {
    const response = await this.request('GET', '/asset-details/filter-options');
    throwIfError(response);
    if (response?.status === 200 && response.data) {
      return {
        makes: Array.isArray(response.data.makes) ? response.data.makes : [],
        locations: Array.isArray(response.data.locations) ? response.data.locations : [],
        processors: Array.isArray(response.data.processors) ? response.data.processors : [],
        assetTypes: Array.isArray(response.data.assetTypes) ? response.data.assetTypes : [],
      };
    }
    return { makes: [], locations: [], processors: [], assetTypes: [] };
  }

  // My Assets self-service: the server derives the caller's own allocations
  // from the JWT — no employee id is sent, so it can never be aimed at
  // someone else.
  static async getMyAssetUtilizes() {
    const response = await this.request('GET', '/asset-utilize/my');
    throwIfError(response);
    if (response?.status === 200 || response?.status === 201) {
      return response.data;
    }
    return [];
  }

  // Audit trail for one asset detail (asset managers only on the BE side —
  // assetdetails.update).
  static async getAssetAuditLog(assetDetailId: number) {
    const response = await this.request('GET', `/asset-details/${assetDetailId}/audit-log`);
    throwIfError(response);
    if (response?.status === 200 || response?.status === 201) {
      return response.data;
    }
    return [];
  }

  static async getAssetUtilizeById(id: any) {
    const response = await this.request('GET', `/asset-utilize/${id}`);
    if (response?.status === 200 || response?.status === 201) {
      return response.data;
    }
    return false;
  }

  static async deleteAssetUtilize(id: any) {
    return this.request('DELETE', `/asset-utilize/${id}`);
  }

  static async updateAssetUtilize(id: any, formdata: any) {
    const response = await this.request('PUT', `/asset-utilize/${id}`, formdata, undefined, 'application/json');
    return response;
  }

  // Record a return (and the asset's condition) in ONE server transaction.
  static async returnAssetUtilize(id: any, payload: { returnedDate: string; ticketIdReturn?: string; remark?: string; assetCondition?: string }) {
    return this.request('POST', `/asset-utilize/${id}/return`, payload, undefined, 'application/json');
  }

  // Link an unlinked legacy allocation to an employee (party) of the tenant.
  static async linkAssetUtilizeEmployee(id: any, employeePartyId: number) {
    return this.request('PATCH', `/asset-utilize/${id}/employee`, { employeePartyId }, undefined, 'application/json');
  }

  // Unlinked allocations whose code matches exactly one employee (suggested match).
  static async getAssetUtilizeLinkReview() {
    const response = await this.request('GET', '/asset-utilize/link-review');
    throwIfError(response);
    return Array.isArray(response?.data) ? response.data : [];
  }

  static async createAssetService(formdata: any) {
    return this.request('POST', '/asset-service', formdata, undefined, 'application/json');
  }

  static async getAllAssetService(callback: any) {
    return this.request('GET', '/asset-service', null, callback);
  }

  static async getAssetServiceById(id: any) {
    const response = await this.request('GET', `/asset-service/${id}`);
    if (response?.status === 200 || response?.status === 201) {
      return response.data;
    }
    return false;
  }

  static async deleteAssetService(id: any) {
    return this.request('DELETE', `/asset-service/${id}`);
  }

  static async updateAssetService(id: any, formdata: any) {
    return this.request('PUT', `/asset-service/edit/${id}`, formdata, undefined, 'application/json');
  }

  static async createAssetExitForm(formdata: any) {
    return this.request('POST', '/asset-exit-form', formdata, undefined, 'application/json');
  }

  static async updateAssetExitForm(id: any, formdata: any) {
    return this.request('PUT', `/asset-exit-form/edit/${id}`, formdata, undefined, 'application/json');
  }

  static async getAllAssetExit(callback: any) {
    return this.request('GET', '/asset-exit-form', null, callback);
  }

  static async getAssetExitDetailsById(id: any) {
    const response = await this.request('GET', `/asset-exit-form/${id}`);
    if (response?.status === 200 || response?.status === 201) {
      return response.data;
    }
    return false;
  }

  static async deleteAssetExit(id: any) {
    return this.request('DELETE', `/asset-exit-form/${id}`);
  }

  // Multi-asset exit clearance (plan §6.3)
  static async getAssetClearanceHolders(search: string) {
    return this.request('GET', '/asset-exit-form/holders', null, undefined, 'application/json', { search });
  }

  static async getAssetClearanceHeld(partyId: number | string) {
    return this.request('GET', `/asset-exit-form/held/${partyId}`);
  }

  static async createAssetClearance(payload: any) {
    return this.request('POST', '/asset-exit-form/clearance', payload, undefined, 'application/json');
  }

  static async getAssetExitByClearanceRef(clearanceRef: string, callback: any) {
    return this.request('GET', '/asset-exit-form', null, callback, 'application/json', { clearanceRef });
  }

  static async getLeaveLedgerInfo() {
    const response = await this.request('GET', '/leave_ledger');
    return response?.data;
  }
  static async getMonthlyAttendanceReport(employeeId: any, month: any) {
    return this.request('GET', `/bio-metric-connection/monthly-attendance?month=${encodeURIComponent(month)}&employeeId=${employeeId}`);
  }
  static async getDailyAttendanceBiometricData() {
    return this.request('GET', '/bio-metric-connection/daily-attendance');
  }

  static async getDepartmentLeaves(departmentId: string, month: number, year: number) {
    const params = new URLSearchParams({
      departmentId,
      month: month.toString(),
      year: year.toString()
    }).toString();

    return this.request('GET', `/leave-balance/department-leaves?${params}`);
  }

  static async getCalenderLeaves() {
    return this.request('GET', `leave_transaction`);
  }

  // Holiday Policy
  static async createPolicy(formData: any, callback: any) {
    return this.request('POST', '/policy', formData, callback);
  }
  static async updatePolicyById(id: number, formData, callback: any) {
    return this.request('PUT', `/policy/${id}`, formData, callback);
  }
  static async getAllPolicy(callback: any) {
    return this.request('GET', '/policy', null, callback);
  }
  static async getPolicyById(id: number) {
    return this.request('GET', `/policy/${id}`);
  }

  static async deletePolicy(id: any) {
    return this.request('DELETE', `/policy/delete/${id}`);
  }

  // Holiday calender

  static async getAllHolidays(callback: any) {
    return this.request('GET', '/holiday-calender', null, callback);
  }

  static async createHolidays(payload: any, callback: any) {
    return this.request('POST', '/holiday-calender', payload, callback);
  }

  static async getHolidayCalenderById(id: number) {
    return this.request('GET', `/holiday-calender/${id}`);
  }

  static async updateHolidayById(id: number, payload: any) {
    return this.request('PUT', `/holiday-calender/${id}`, payload);
  }

  static async deleteHolidayCalenderById(id: string, callback: any) {
    return this.request('DELETE', `/holiday-calender/${id}`, null, callback);
  }

  static async getHolidaysByLocation(locationId: number, year: number, callback: any) {
    return this.request('GET', `/holiday-calender/by-location/${locationId}/year/${year}`, null, callback);
  }

  // ── Geo endpoints ──────────────────────────────────────────────────────────

  /**
   * List countries from the geo service.
   * Returns records shaped { id, name, iso2, currencyCode, currencySymbol, defaultTimezone, dateFormat, ... }
   * @param search Optional free-text search string (forwarded as ?search=<q>)
   * @param callback Called with the axios response (data array at response.data or response)
   */
  static async listGeoCountries(search: string | null | undefined, callback: (res: any) => void) {
    const path = search
      ? `v1/geo/countries?search=${encodeURIComponent(search)}`
      : 'v1/geo/countries';
    return this.request('GET', path, null, callback, 'application/json', undefined, true);
  }

  /**
   * List states/regions for a given country.
   * Returns records shaped { id, name, state_code, timezone, ... }
   * @param countryId Numeric country ID
   * @param search Optional free-text search string (forwarded as ?search=<q>)
   * @param callback Called with the axios response
   */
  static async listGeoStates(countryId: number, search: string | null | undefined, callback: (res: any) => void) {
    const path = search
      ? `v1/geo/countries/${countryId}/states?search=${encodeURIComponent(search)}`
      : `v1/geo/countries/${countryId}/states`;
    return this.request('GET', path, null, callback, 'application/json', undefined, true);
  }

  // Chat Bot
  static async chatBotFaq(message: string) {
    return this.request('GET', `/chat-bot/faq?q=${message}`);
  }

  // Shift Plan
  static async createShiftPlan(payload: any, callback: any) {
    return this.request('POST', '/shifts', payload, callback);
  }
  static async getAllShiftPlan(callback: any, params?: { startDate?: string; endDate?: string }) {
    return this.request('GET', '/shifts', null, callback, null, params);
  }
  static async getShiftPlanById(id: string) {
    return this.request('GET', `/shifts/${id}`, null);
  }

  static async getShiftPlanByEmployeeId(employeeId: string) {
    return this.request('GET', `/shifts/employee/${employeeId}`, null);
  }

  static async getShiftPlanByToday(employeeId: string, callback: any) {
    return this.request('GET', `/shifts/today/${employeeId}`, null, callback);
  }

  static async updateShiftPlan(id: number, payload: any) {
    return this.request('PUT', `/shifts/${id}`, payload);
  }

  // Shift Plan — "employees with no shift assigned" report
  static async getUnassignedShiftEmployees(
    params: { from: string; to: string },
    callback?: any
  ) {
    return this.request('GET', '/shifts/unassigned', null, callback, null, params);
  }

  // Shift Plan — Plan-level API (v2: plan UUIDs, not record IDs)
  static async getShiftPlans(callback?: any) {
    return this.request('GET', '/shifts/plans', null, callback);
  }

  // Paginated Patterns list: { data, meta: { total, limit, offset }, counts }. Empty params are
  // dropped, but sending ANY of these opts in to the envelope (the bare array is getShiftPlans).
  static async getShiftPlansPage(
    params: { limit?: number; offset?: number; search?: string; status?: string },
    callback?: any
  ) {
    const query: Record<string, string | number> = {};
    if (params.limit !== undefined) query.limit = params.limit;
    if (params.offset !== undefined) query.offset = params.offset;
    if (params.search) query.search = params.search;
    if (params.status) query.status = params.status;
    return this.request('GET', '/shifts/plans', null, callback, null, query);
  }

  static async getShiftPlanByPlanId(planId: string, callback?: any) {
    return this.request('GET', `/shifts/plans/${planId}`, null, callback);
  }

  static async updateShiftPlan2(
    planId: string,
    body: {
      shiftType: string;
      startDate: string;
      endDate: string;
      daysOfWeek: number[];
    },
    callback?: any
  ) {
    return this.request('PUT', `/shifts/plans/${planId}`, body, callback);
  }

  static async deleteShiftPlan(planId: string, callback?: any) {
    return this.request('DELETE', `/shifts/plans/${planId}`, null, callback);
  }

  /**
   * Sets whether a plan's rostered days override the work location's holiday
   * calendar (the 24x7 case).
   *
   * Separate from updateShiftPlan2 on purpose, and it must stay that way: the
   * plan-update payload does not carry this flag, so folding the two together
   * would make every routine roster edit silently reset a 24x7 plan back to
   * respecting holidays — which would quietly hand a public holiday back to
   * everyone on that rota.
   */
  static async setShiftPlanHolidayOverride(
    planId: string,
    worksThroughHolidays: boolean,
    callback?: any
  ) {
    return this.request(
      'PATCH',
      `/shifts/plans/${planId}/holiday-override`,
      { worksThroughHolidays },
      callback
    );
  }

  // shift Timing

  static async getShiftTiming(callback: any) {
    return this.request('GET', '/shift-times', null, callback);
  }

  static async getShiftTimingById(id, callback: any) {
    return this.request('GET', `/shift-times/${id}`, null, callback);
  }

  static async getShiftTimingWithFilter(filters: any, callback: any) {
    return this.request('POST', '/shift-times/filter', filters, callback);
  }

  static async createShiftTiming(payload: any, callback: any) {
    return this.request('POST', '/shift-times', payload, callback);
  }

  static async updateShiftTiming(id, payload: any, callback: any) {
    return this.request('PUT', `/shift-times/${id}`, payload, callback);
  }

  static async deleteShiftTimingById(id, callback: any) {
    return this.request('DELETE', `/shift-times/${id}`, null, callback);
  }

  // Swap Shift Plan
  static async createSwapShift(payload: any, callback: any) {
    return this.request('POST', '/swap-shifts', payload, callback);
  }

  static async updateSwapShift(id: any, payload: any, callback: any) {
    return this.request('PUT', `/swap-shifts/${id}`, payload, callback);
  }

  static async getAllSwapShifts(callback: any, params?: { startDate?: string; endDate?: string }) {
    return this.request('GET', '/swap-shifts', null, callback, null, params);
  }

  // Caller's Shift Swap Request scope (role editor): { scope, decideScope, canSeeTeam }
  static async getSwapShiftScope(callback?: any) {
    return this.request('GET', '/swap-shifts/scope', null, callback);
  }

  static async getSwapShiftById(id: any, callback: any) {
    return this.request('GET', `/swap-shifts/${id}`, null, callback);
  }

  static async getSwapShiftByEmployeeId(employeeId: number, callback?: any, extraConfig?: Record<string, any>) {
    return this.request('GET', `/swap-shifts/employee/${employeeId}`, null, callback, 'application/json', undefined, true, extraConfig);
  }

  // Shift Coverage

  static async createShiftCoverage(payload: any, callback: any) {
    // maxTouchPoints lets the backend tell an iPad (which sends a desktop
    // "Macintosh" User-Agent in iPadOS's default desktop-website mode) from a
    // real Mac for the Permission Policy's allowed-login-source check.
    const maxTouchPoints =
      typeof navigator !== 'undefined' && typeof navigator.maxTouchPoints === 'number' ? navigator.maxTouchPoints : 0;
    // expectedStatuses: [403] — a device the Permission Policy disallows is a
    // NORMAL outcome for this call, with its own specific "Unauthorized: shift
    // login from …" dialog (PostLoginPage.tsx / addShiftCoverage.tsx). Without
    // this, the global interceptor's generic "Permission Denied: You don't
    // have permission to do that." toast fired at the same time, showing the
    // user two overlapping popups for one rejection.
    return this.request(
      'POST',
      '/shift-coverage',
      { maxTouchPoints, ...payload },
      callback,
      'application/json',
      undefined,
      true,
      { expectedStatuses: [403] }
    );
  }

  static async createShiftExtendHour(payload: any, callback: any) {
    return this.request('POST', '/shift-coverage/extend-hour', payload, callback);
  }

  static async getAllShiftCoverage(callback: any, params?: { startDate?: string; endDate?: string }) {
    return this.request('GET', '/shift-coverage', null, callback, null, params);
  }

  static async getShiftCoverageByUser(userId: any, callback: any) {
    return this.request('GET', `/shift-coverage/user/${userId}`, null, callback);
  }

  static async getAllShiftCoverageWithExtendHours(callback: any, params?: { startDate?: string; endDate?: string }) {
    return this.request('GET', '/shift-coverage/extend-hour', null, callback, null, params);
  }

  static async getShiftCoverageWithExtendHourByUser(userId: any, callback: any) {
    return this.request('GET', `/shift-coverage/extend-hour/user/${userId}`, null, callback);
  }

  static async getShiftCoverageByUserDate(userId: any, date: string, callback: any) {
    const params = {
      date: date
    };
    return this.request('GET', `/shift-coverage/user/${userId}`, null, callback, null, params);
  }

  static async updateShiftCoverage(id: number, payload: any) {
    return this.request('PUT', `/shift-coverage/${id}`, payload);
  }

  static async updateShiftCoverageManualLogouttime(id: number, payload: any) {
    return this.request('PUT', `/shift-coverage/${id}/end-time`, payload);
  }

  static async updateShiftExtendHours(id: number, payload: any) {
    return this.request('PUT', `/shift-coverage/extend-hour/${id}`, payload);
  }

  static async updateShiftExtendHoursManualLogouttime(id: number, payload: any) {
    return this.request('PUT', `/shift-coverage/extend-hour/${id}/end-time`, payload);
  }


  // shift Time

  static async getShiftTime(callback: any) {
    return this.request('GET', '/shift-times', null, callback);
  }

  // Shift Leave Type

  static async CreateShiftLeaveType(payload: any, callback: any) {
    return this.request('POST', '/shift-leave-type', payload, callback);
  }

  static async getAllShiftLeaveType(callback: any) {
    return this.request('GET', '/shift-leave-type', null, callback);
  }

  static async verifyLocation(payload: { username: string; latitude: number; longitude: number }, callback: any) {
    return this.request('POST', '/auth/verify-location', payload, callback);
  }

  static async updateShiftLeaveType(id: number, payload: any) {
    return this.request('PUT', `/shift-leave-type/${id}`, payload);
  }
  static async deleteShiftLeaveTypeById(id: string, callback: any) {
    return this.request('DELETE', `/shift-leave-type/${id}`, callback);
  }

  // Attendance

  static async fetchAllAttendance(params, callback: any) {
    return this.request('GET', 'shift-coverage/attendance', null, callback, null, params);
  }

  static async fetchAllAttendanceSummary(params, callback: any) {
    return this.request('GET', 'shift-coverage/attendance-summary', null, callback, null, params);
  }

  static async fetchTodayAttendanceSummary(params, callback: any) {
    return this.request('GET', 'shift-coverage/today-attendance-summary', null, callback, null, params);
  }

  static async getEmployeeSalaryDetails(employeeId: number, params: any, callback: any) {
    return this.request('GET', `employee/salary-details/${employeeId}`, null, callback, null, params);
  }
  // Ticket Transition

  static async CreateTicketTransition(payload: any, callback: any) {
    return this.request('POST', '/ticket-transitions', payload, callback);
  }

  static async CreateTicketHandoverMail(payload: any, callback: any) {
    return this.request('POST', '/ticket-transitions/sendHandoverMail', payload, callback);
  }

  static async getAllTicketTransition(callback: any) {
    return this.request('GET', '/ticket-transitions', null, callback);
  }

  static async getTicketTransitionById(id: string) {
    return this.request('GET', `/ticket-transitions/${id}`, null);
  }

  static async updateTicketTransitionById(id: number, payload: any) {
    return this.request('PUT', `/ticket-transitions/${id}`, payload);
  }

  static async getTicketTransitionId(id: string) {
    const response = await this.request('GET', `/ticket-transitions/${id}`);
    if (response?.status === HttpStatusCode.Ok) {
      return response?.data;
    }
    return false;
  }

  // Feature Role Access

  static async createFeatureRoleAccess(payload: any, callback: any) {
    return this.request('POST', '/feature-role-access', payload, callback);
  }
  static async getAllFeatureRoleAccess(callback: any) {
    return this.request('GET', '/feature-role-access', null, callback);
  }

  static async getlFeatureRoleAccessByReferenceName(params: any, callback: any) {
    return this.request('GET', '/feature-role-access/reference/name', null, callback, null, params);
  }

  static async getFeatureRoleAccessById(id: string, callback: any) {
    return this.request('GET', `/feature-role-access/${id}`, null, callback);
  }

  static async updateFeatureRoleAccess(id: string, payload: any, callback: any) {
    return this.request('PUT', `/feature-role-access/${id}`, payload, callback);
  }

  // Permission

  static async createPermissionRequest(payload: any, callback: any) {
    return this.request('POST', '/permissions_data', payload, callback);
  }

  static async getAllPermissions(callback: any) {
    return this.request('GET', '/permissions_data', null, callback);
  }

  static async getPermissioByEmployeeId(partyId: string) {
    return this.request('GET', `/permissions_data/party/${partyId}`, null);
  }
  static async updatePermissioById(id: number, payload: any) {
    return this.request('PUT', `/permissions_data/${id}`, payload);
  }

  static async createTicketClosure(payload: any, callback: any) {
    return this.request('POST', '/ticket-closure', payload, callback);
  }

  static async getAllTicketClosureWithFilters(payload: any, callback: any) {
    return this.request('POST', '/ticket-closure/search', payload, callback);
  }

  static async updateTicketClosure(id: number, payload: any, callback: any) {
    return this.request('PUT', `/ticket-closure/${id}`, payload, callback);
  }

  static async getTicketClosure(page = 1, limit = 10, callback: any) {
    const query = `?page=${page}&limit=${limit}`;
    return this.request('GET', `/ticket-closure${query}`, callback);
  }

  static async ticketClosureBulkUpdate(formdata: any[], callback: any) {
    return this.request('POST', '/ticket-closure/bulkupdate', formdata, callback);
  }

  static async postPermissioReplyById(id: number, payload: any) {
    return this.request('POST', `/permissions_data/${id}/reply`, payload);
  }

  static async getDefectTrackerList(page, limit, callback: any) {
    const query = `?page=${page}&limit=${limit}`;
    return this.request('GET', `/defect-tracker${query}`, null, callback);
  }

  static async createDefect(payload: any, callback: any) {
    return this.request('POST', `/defect-tracker`, payload, callback);
  }

  static async getDefectById(id: string, callback: any) {
    return this.request('GET', `/defect-tracker/${id}`, null, callback);
  }

  // Update defect
  static async updateDefect(id: string, payload: any, callback: any) {
    return this.request('PUT', `/defect-tracker/${id}`, payload, callback);
  }
  static async createDefectCommand(payload: any, callback?: any) {
    return this.request('POST', '/defect-tracker-commands', payload, callback);
  }

  static async createDefectWithFiles(formData: FormData, callback: any) {
    return this.request('POST', `/defect-tracker`, formData, callback);
  }

  // Update defect with files
  static async updateDefectWithFiles(id: number, formData: FormData, callback: any) {
    return this.request('PUT', `/defect-tracker/${id}`, formData, callback);
  }
  static async uploadCommandFiles(formData: FormData, callback: any) {
    return this.request('POST', `/defect-tracker-commands/upload-files`, formData, callback);
  }

  static async createDefectCommandWithFiles(formData: FormData, callback?: any) {
    return this.request('POST', '/defect-tracker-commands/with-files', formData, callback);
  }


  // Loan Request
  static async createLoanRequest(formData: any, callback: any) {
    return this.request('POST', `/loan-request`, formData, callback);
  }

  static async getLoanRequests(
    params: {
      employeeId?: number;
      statusId?: number;
      page?: number;
      limit?: number;
      withoutPagination?: boolean;
    },
    callback?: any
  ) {
    const query = new URLSearchParams();

    if (params.employeeId) query.append('employeeId', params.employeeId.toString());
    if (params.statusId) query.append('statusId', params.statusId.toString());
    if (params.page) query.append('page', params.page.toString());
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.withoutPagination !== undefined) query.append('withoutPagination', params.withoutPagination ? 'true' : 'false');

    return this.request('GET', `/loan-request?${query.toString()}`, null, callback);
  }

  static async getLoanRequestById(id: any, callback: any) {
    return this.request('GET', `/loan-request/${id}`, null, callback);
  }

  static async updateLoanRequest(id: any, formData: any, callback: any) {
    return this.request('PATCH', `/loan-request/${id}`, formData, callback);
  }

  // Salary Advance

  static async createSalaryAdvance(formData: any, callback: any) {
    return this.request('POST', `/salary-advance`, formData, callback);
  }

  static async getSalaryAdvances(
    params: {
      employeeId?: number;
      statusId?: number;
      page?: number;
      limit?: number;
      withoutPagination?: boolean;
    },
    callback?: any
  ) {
    const query = new URLSearchParams();

    if (params.employeeId) query.append('employeeId', params.employeeId.toString());
    if (params.statusId) query.append('statusId', params.statusId.toString());
    if (params.page) query.append('page', params.page.toString());
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.withoutPagination !== undefined) query.append('withoutPagination', params.withoutPagination ? 'true' : 'false');

    return this.request('GET', `/salary-advance?${query.toString()}`, null, callback);
  }

  static async getSalaryAdvanceById(id: any, callback: any) {
    return this.request('GET', `/salary-advance/${id}`, null, callback);
  }

  static async updateSalaryAdvance(id: any, formData: any, callback: any) {
    return this.request('PATCH', `/salary-advance/${id}`, formData, callback);
  }

  // leave Balance History
  static async createLeaveBalanceHistory(data: any, callback?: any) {
    return this.request('POST', '/leave-balance-history', data, callback);
  }

  static async getLeaveBalanceHistory(
    params: {
      offset?: number;
      limit?: number;
      [key: string]: any;
    },
    callback?: any
  ) {
    const queryParams = new URLSearchParams(params).toString();

    return this.request('GET', `/leave-balance-history`, null, callback, null, params);
  }

  static async getAllLeaveBalanceHistory(callback?: any) {
    return this.request('GET', '/leave-balance-history/all', null, callback);
  }

  static async getLeaveBalanceHistoryByUser(userId: any, leaveTypeId?: number, callback?: any) {
    const params = leaveTypeId ? { leaveTypeId } : null;

    return this.request('GET', `/leave-balance-history/user/${userId}`, null, callback, null, params);
  }

  static async getLeaveBalanceHistoryById(id: number, callback?: any) {
    return this.request('GET', `/leave-balance-history/${id}`, null, callback);
  }

  static async updateLeaveBalanceHistory(id: number, data: any, callback?: any) {
    return this.request('PATCH', `/leave-balance-history/${id}`, data, callback);
  }

  // ── Admin Device Management ─────────────────────────────────────────────
  static async getAdminDevices(params?: Record<string, string>, callback?: any) {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request('GET', `/admin/devices${query}`, null, callback);
  }

  static async getAdminDeviceById(id: string, callback?: any) {
    return this.request('GET', `/admin/devices/${id}`, null, callback);
  }

  static async createAdminDevice(data: any, callback?: any) {
    return this.request('POST', '/admin/devices', data, callback);
  }

  static async updateAdminDevice(id: string, data: any, callback?: any) {
    return this.request('PUT', `/admin/devices/${id}`, data, callback);
  }

  static async deleteAdminDevice(id: string, callback?: any) {
    return this.request('DELETE', `/admin/devices/${id}`, null, callback);
  }

  static async generateDeviceCode(id: string, callback?: any) {
    return this.request('POST', `/admin/devices/${id}/generate-code`, null, callback);
  }

  static async generateDeviceToken(id: string, callback?: any) {
    return this.request('POST', `/admin/devices/${id}/generate-token`, null, callback);
  }

  static async getAdminDeviceStats(callback?: any) {
    return this.request('GET', '/admin/devices/stats', null, callback);
  }

  // ── Attendance PIN Enrolment Mapping (Attendance v2 Phase 0) ─────────────
  static async getUnresolvedEnrolments(callback?: any) {
    return this.request('GET', 'v1/attendance/enrolments/unresolved', null, callback);
  }

  static async getEnrolments(params?: { devicePin?: string; employeeId?: string | number }, callback?: any) {
    return this.request('GET', 'v1/attendance/enrolments', null, callback, 'application/json', params);
  }

  static async createEnrolment(data: any, callback?: any) {
    return this.request('POST', 'v1/attendance/enrolments', data, callback);
  }

  // ── Attendance v2 Phase 4 — read-only workday sheet + provenance ─────────
  // Gated on 'attendanceworkday.read' (see backend page_bundles.go). Neither
  // endpoint writes anything — see routes.go's package doc on the Go side.
  static async getAttendanceWorkdaySheet(
    params: { from: string; to: string; employeeId?: number; workLocation?: string },
    callback?: any
  ) {
    const queryParams: Record<string, string> = { from: params.from, to: params.to };
    if (params.employeeId != null) queryParams.employeeId = String(params.employeeId);
    if (params.workLocation) queryParams.workLocation = params.workLocation;
    return this.request('GET', 'v1/attendance/workday', null, callback, 'application/json', queryParams);
  }

  // ── Shift Automation Policy (Organisation Settings → Shift automation) ──
  // GET is readable by any authenticated tenant user; PUT is tenant-admin
  // only (the response's `editable` flag says which, so the UI can disable
  // its controls rather than failing on save).
  static async getShiftAutomationPolicy(callback?: any) {
    return this.request('GET', 'v1/shift-automation-policy', null, callback);
  }

  static async updateShiftAutomationPolicy(body: any, callback?: any) {
    return this.request('PUT', 'v1/shift-automation-policy', body, callback);
  }

  // ── Tenant feature settings (Organisation Settings → Feature settings) ──
  // Typed settings API (backend tenants module). GET is tenant-admin only; a
  // 403 is a normal outcome the screen words itself, so no global toast. PUT
  // answers 400 (per-field errors), 403, 404 and 409 (STALE_VALUE and friends)
  // that the row renders inline, so those are also kept out of the toast path.
  static async getTenantFlags(callback?: any) {
    return this.request('GET', 'tenants/flags', null, callback, undefined, undefined, true, {
      expectedStatuses: [403]
    });
  }

  static async setTenantFlag(id: string, body: { value: unknown; expected?: unknown }, callback?: any) {
    return this.request('PUT', `tenants/flags/${encodeURIComponent(id)}`, body, callback, undefined, undefined, true, {
      expectedStatuses: [400, 403, 404, 409]
    });
  }

  static async getAttendanceWorkdayProvenance(employeeId: number, date: string, callback?: any) {
    return this.request('GET', `v1/attendance/workday/${employeeId}/${date}`, null, callback);
  }

  // Shift plans v2 slice 11: stored Layer 1 (shift, source, holiday, night/
  // rotational, legacy H/L) + the amendment history from shift_change_log.
  // attendanceworkday.read, or the caller's own employee-day.
  static async getAttendanceWorkdayRosterProvenance(employeeId: number, date: string, callback?: any) {
    return this.request('GET', `v1/attendance/workday/${employeeId}/${date}/roster`, null, callback);
  }

  // Shift plans v2 slice 12 ("My attendance"): the caller's OWN stored days.
  // The employee is resolved server-side from the JWT party id — no id is sent,
  // and no attendanceworkday.* grant is needed.
  static async getMyAttendanceWorkdayDays(params: { from: string; to: string }, callback?: any) {
    return this.request('GET', 'v1/attendance/workday/me', null, callback, 'application/json', {
      from: params.from,
      to: params.to
    });
  }

  // ── As-of-time Attendance Summary Report ─────────────────────────────────
  // Gated on 'attendancesummaryreport.read' (see backend page_bundles.go).
  static async getAttendanceSummaryReport(
    params: { reportDate: string; viewingTime: string; workLocationId?: number },
    callback?: any
  ) {
    const queryParams: Record<string, string> = {
      reportDate: params.reportDate,
      viewingTime: params.viewingTime
    };
    if (params.workLocationId != null) queryParams.workLocationId = String(params.workLocationId);
    return this.request('GET', 'v1/attendance/summary-report', null, callback, 'application/json', queryParams);
  }

  // ── Attendance v2 Phase 5 — manual marking (present/absent) ──────────────
  // Gated on 'attendanceworkday.mark' (see backend page_bundles.go); List is
  // gated on the read permission (attendanceworkday.read), same as the sheet.
  static async getAttendanceWorkdayMarks(
    params: { from: string; to: string; employeeId?: number },
    callback?: any
  ) {
    const queryParams: Record<string, string> = { from: params.from, to: params.to };
    if (params.employeeId != null) queryParams.employeeId = String(params.employeeId);
    return this.request('GET', 'v1/attendance/workday/marks', null, callback, 'application/json', queryParams);
  }

  static async createAttendanceWorkdayMark(data: any, callback?: any) {
    return this.request('POST', 'v1/attendance/workday/marks', data, callback);
  }

  static async approveAttendanceWorkdayMark(id: number | string, data: any, callback?: any) {
    return this.request('POST', `v1/attendance/workday/marks/${id}/approve`, data, callback);
  }

  static async declineAttendanceWorkdayMark(id: number | string, data: any, callback?: any) {
    return this.request('POST', `v1/attendance/workday/marks/${id}/decline`, data, callback);
  }

  static async updateEnrolment(id: string | number, data: any, callback?: any) {
    return this.request('PUT', `v1/attendance/enrolments/${id}`, data, callback);
  }

  static async deleteEnrolment(id: string | number, callback?: any) {
    return this.request('DELETE', `v1/attendance/enrolments/${id}`, null, callback);
  }

  // Employee numbering rules
  static async listNumberingRules(callback?: any) {
    return this.request('GET', '/settings/employee-numbering/rules', null, callback);
  }

  static async getNumberingRule(id: number, callback?: any) {
    return this.request('GET', `/settings/employee-numbering/rules/${id}`, null, callback);
  }

  static async createNumberingRule(data: any, callback?: any) {
    return this.request('POST', '/settings/employee-numbering/rules', data, callback);
  }

  static async updateNumberingRule(id: number, data: any, callback?: any) {
    return this.request('PUT', `/settings/employee-numbering/rules/${id}`, data, callback);
  }

  static async deleteNumberingRule(id: number, callback?: any) {
    return this.request('DELETE', `/settings/employee-numbering/rules/${id}`, null, callback);
  }

  static async setDefaultNumberingRule(id: number, callback?: any) {
    return this.request('POST', `/settings/employee-numbering/rules/${id}/set-default`, {}, callback);
  }

  static async previewEmployeeId(data: any, callback?: any) {
    return this.request('POST', '/settings/employee-numbering/preview', data, callback);
  }

  // ===== OVERTIME =====

  static async getOvertimePolicies(callback?: any) {
    return this.request('GET', '/v1/overtime/policies', null, callback);
  }

  static async getOvertimePolicyById(id: number | string, callback?: any) {
    return this.request('GET', `/v1/overtime/policies/${id}`, null, callback);
  }

  static async createOvertimePolicy(data: any, callback?: any) {
    return this.request('POST', '/v1/overtime/policies', data, callback);
  }

  static async updateOvertimePolicy(id: number | string, data: any, callback?: any) {
    return this.request('PUT', `/v1/overtime/policies/${id}`, data, callback);
  }

  static async deleteOvertimePolicy(id: number | string, callback?: any) {
    return this.request('DELETE', `/v1/overtime/policies/${id}`, null, callback);
  }

  static async getOvertimeRecords(params: {
    status?: string;
    partyId?: number | string;
    from?: string;
    to?: string;
    page?: number;
    pageSize?: number;
  }, callback?: any, extraConfig?: Record<string, any>) {
    const query = new URLSearchParams();
    if (params.status && params.status !== 'all') query.set('status', params.status);
    if (params.partyId) query.set('partyId', String(params.partyId));
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));
    const qs = query.toString();
    return this.request('GET', `/v1/overtime/records${qs ? `?${qs}` : ''}`, null, callback, 'application/json', undefined, true, extraConfig);
  }

  static async getOvertimeRecordById(id: number | string, callback?: any) {
    return this.request('GET', `/v1/overtime/records/${id}`, null, callback);
  }

  static async createOvertimeRecord(data: any, callback?: any) {
    return this.request('POST', '/v1/overtime/records', data, callback);
  }

  // The inbox is the module's decision list (#328): direct reports' pending rows, or every
  // pending row for role-name admins. Response: { data: RecordResponse[], total }.
  static async getOvertimeInbox(callback?: any, params?: { page?: number; pageSize?: number }, extraConfig?: Record<string, any>) {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('pageSize', String(params.pageSize));
    const qs = query.toString();
    return this.request('GET', `/v1/overtime/records/inbox${qs ? `?${qs}` : ''}`, null, callback, 'application/json', undefined, true, extraConfig);
  }

  static async updateOvertimeRecordStatus(
    id: number | string,
    payload: { status: 'approved' | 'rejected'; reason?: string },
    callback?: any
  ) {
    return this.request('PATCH', `/v1/overtime/records/${id}/status`, payload, callback);
  }

  // ===== END OVERTIME =====

  // ===== SHIFT LOCATION MAPPING =====

  /** Returns all shift ↔ location mappings for the current tenant. */
  static async getShiftLocationMappings(callback?: any) {
    return this.request('GET', '/v1/shift-location-mappings', null, callback);
  }

  /** Returns mappings for a specific work location. */
  static async getMappingsForLocation(locationId: number, callback?: any) {
    return this.request('GET', `/v1/shift-location-mappings/by-location/${locationId}`, null, callback);
  }

  /** Returns mappings for a specific shift. */
  static async getMappingsForShift(shiftTimesId: number, callback?: any) {
    return this.request('GET', `/v1/shift-location-mappings/by-shift/${shiftTimesId}`, null, callback);
  }

  /** Creates a single shift ↔ location mapping. */
  static async createShiftLocationMapping(payload: { shiftTimesId: number; workLocationId: number }, callback?: any) {
    return this.request('POST', '/v1/shift-location-mappings', payload, callback);
  }

  /** Replaces all mappings for a shift with the provided work-location IDs.
   *  Pass an empty array to remove all mappings for the shift.
   */
  static async replaceMappingsForShift(shiftTimesId: number, payload: { workLocationIds: number[] }, callback?: any) {
    return this.request('PUT', `/v1/shift-location-mappings/shift/${shiftTimesId}`, payload, callback);
  }

  /** Deletes a specific shift ↔ location mapping by UUID. */
  static async deleteShiftLocationMapping(id: string, callback?: any) {
    return this.request('DELETE', `/v1/shift-location-mappings/${id}`, null, callback);
  }

  // ===== END SHIFT LOCATION MAPPING =====

  // ===== EMPLOYEE LOCATIONS =====

  /** Returns every active (primary + secondary) work-location membership for an employee. */
  static async getEmployeeLocations(employeeId: number, callback?: any) {
    return this.request('GET', `/v1/employee-locations/${employeeId}`, null, callback);
  }

  /** Adds a secondary work location to an employee. */
  static async addEmployeeLocation(employeeId: number, workLocationId: number, callback?: any) {
    return this.request('POST', `/v1/employee-locations/${employeeId}`, { workLocationId }, callback);
  }

  /** Removes a secondary work-location membership by its row id. Cannot remove the primary location. */
  static async removeEmployeeLocation(employeeId: number, locationId: string, callback?: any) {
    return this.request('DELETE', `/v1/employee-locations/${employeeId}/${locationId}`, null, callback);
  }

  /** Promotes an already-assigned work location to primary. Also updates employee.workLocationId. */
  static async setPrimaryEmployeeLocation(employeeId: number, workLocationId: number, callback?: any) {
    return this.request('PATCH', `/v1/employee-locations/${employeeId}/primary`, { workLocationId }, callback);
  }

  // ===== END EMPLOYEE LOCATIONS =====

  // ===== SHIFT ROTATIONS =====

  static async getShiftRotations(activeOnly?: boolean, callback?: any) {
    const qs = activeOnly ? '?activeOnly=true' : '';
    return this.request('GET', `/v1/shift-rotations${qs}`, null, callback);
  }

  static async getShiftRotationById(id: number | string, callback?: any) {
    return this.request('GET', `/v1/shift-rotations/${id}`, null, callback);
  }

  static async createShiftRotation(data: any, callback?: any) {
    return this.request('POST', '/v1/shift-rotations', data, callback);
  }

  static async updateShiftRotation(id: number | string, data: any, callback?: any) {
    return this.request('PUT', `/v1/shift-rotations/${id}`, data, callback);
  }

  static async deleteShiftRotation(id: number | string, callback?: any) {
    return this.request('DELETE', `/v1/shift-rotations/${id}`, null, callback);
  }

  static async getShiftRotationAssignments(rotationId: number | string, callback?: any) {
    return this.request('GET', `/v1/shift-rotations/${rotationId}/assignments`, null, callback);
  }

  static async createShiftRotationAssignment(rotationId: number | string, data: any, callback?: any) {
    return this.request('POST', `/v1/shift-rotations/${rotationId}/assignments`, data, callback);
  }

  static async deleteShiftRotationAssignment(id: number | string, callback?: any) {
    return this.request('DELETE', `/v1/shift-rotation-assignments/${id}`, null, callback);
  }

  static async triggerShiftRotationMaterialize(callback?: any) {
    return this.request('POST', '/v1/shift-rotations/materialize', null, callback);
  }

  // ===== END SHIFT ROTATIONS =====

  // ===== SHIFT ALLOWANCE =====

  static async getShiftAllowancePolicies(callback?: any) {
    return this.request('GET', '/v1/shift-allowance-policies', null, callback);
  }

  static async getShiftAllowancePolicyById(id: number | string, callback?: any) {
    return this.request('GET', `/v1/shift-allowance-policies/${id}`, null, callback);
  }

  static async createShiftAllowancePolicy(data: any, callback?: any) {
    return this.request('POST', '/v1/shift-allowance-policies', data, callback);
  }

  static async updateShiftAllowancePolicy(id: number | string, data: any, callback?: any) {
    return this.request('PUT', `/v1/shift-allowance-policies/${id}`, data, callback);
  }

  static async deleteShiftAllowancePolicy(id: number | string, callback?: any) {
    return this.request('DELETE', `/v1/shift-allowance-policies/${id}`, null, callback);
  }

  static async getShiftAllowanceRecords(params: {
    status?: string;
    partyId?: number | string;
    periodStart?: string;
    periodEnd?: string;
    page?: number;
    pageSize?: number;
  }, callback?: any) {
    const query = new URLSearchParams();
    if (params.status && params.status !== 'all') query.set('status', params.status);
    if (params.partyId) query.set('partyId', String(params.partyId));
    if (params.periodStart) query.set('periodStart', params.periodStart);
    if (params.periodEnd) query.set('periodEnd', params.periodEnd);
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));
    const qs = query.toString();
    return this.request('GET', `/v1/shift-allowance-records${qs ? `?${qs}` : ''}`, null, callback);
  }

  static async getShiftAllowanceRecordById(id: number | string, callback?: any) {
    return this.request('GET', `/v1/shift-allowance-records/${id}`, null, callback);
  }

  static async triggerShiftAllowanceCalculate(data: { periodStart: string; periodEnd: string }, callback?: any) {
    return this.request('POST', '/v1/shift-allowance-calculate', data, callback);
  }

  // ===== END SHIFT ALLOWANCE =====

  // ===== SHIFT ASSIGNMENT RULES =====

  static async getShiftAssignmentRules(activeOnly?: boolean, callback?: any) {
    const qs = activeOnly ? '?activeOnly=true' : '';
    return this.request('GET', `/v1/shift-assignment-rules${qs}`, null, callback);
  }

  static async getShiftAssignmentRuleById(id: number | string, callback?: any) {
    return this.request('GET', `/v1/shift-assignment-rules/${id}`, null, callback);
  }

  static async createShiftAssignmentRule(data: any, callback?: any) {
    return this.request('POST', '/v1/shift-assignment-rules', data, callback);
  }

  static async updateShiftAssignmentRule(id: number | string, data: any, callback?: any) {
    return this.request('PUT', `/v1/shift-assignment-rules/${id}`, data, callback);
  }

  static async deleteShiftAssignmentRule(id: number | string, callback?: any) {
    return this.request('DELETE', `/v1/shift-assignment-rules/${id}`, null, callback);
  }

  static async triggerShiftAssignmentRulesRun(callback?: any) {
    return this.request('POST', '/v1/shift-assignment-rules/run', null, callback);
  }

  // ===== END SHIFT ASSIGNMENT RULES =====

  // ===== SPECIALIZATIONS =====

  static async getSpecializations(callback?: any) {
    return this.request('GET', '/v1/specializations', null, callback);
  }

  static async getSpecializationById(id: number | string, callback?: any) {
    return this.request('GET', `/v1/specializations/${id}`, null, callback);
  }

  static async createSpecialization(data: any, callback?: any) {
    return this.request('POST', '/v1/specializations', data, callback);
  }

  static async updateSpecialization(id: number | string, data: any, callback?: any) {
    return this.request('PUT', `/v1/specializations/${id}`, data, callback);
  }

  static async deleteSpecialization(id: number | string, callback?: any) {
    return this.request('DELETE', `/v1/specializations/${id}`, null, callback);
  }

  // ===== END SPECIALIZATIONS =====

  // ===== PRACTICE AREAS =====

  static async getPracticeAreas(callback?: any) {
    return this.request('GET', '/v1/practice-areas', null, callback);
  }

  static async getPracticeAreasAll(callback?: any) {
    return this.request('GET', '/v1/practice-areas/all', null, callback);
  }

  static async getPracticeAreaById(id: number | string, callback?: any) {
    return this.request('GET', `/v1/practice-areas/${id}`, null, callback);
  }

  static async createPracticeArea(data: any, callback?: any) {
    return this.request('POST', '/v1/practice-areas', data, callback);
  }

  static async updatePracticeArea(id: number | string, data: any, callback?: any) {
    return this.request('PUT', `/v1/practice-areas/${id}`, data, callback);
  }

  static async deletePracticeArea(id: number | string, callback?: any) {
    return this.request('DELETE', `/v1/practice-areas/${id}`, null, callback);
  }

  static async setPracticeAreaActive(id: number | string, isActive: boolean, callback?: any) {
    return this.request('PATCH', `/v1/practice-areas/${id}/active`, { isActive }, callback);
  }

  static async getPracticeAreaMembers(id: number | string, callback?: any) {
    return this.request('GET', `/v1/practice-areas/${id}/members`, null, callback);
  }

  static async addPracticeAreaMember(id: number | string, data: any, callback?: any) {
    return this.request('POST', `/v1/practice-areas/${id}/members`, data, callback);
  }

  static async removePracticeAreaMember(id: number | string, partyId: number | string, callback?: any) {
    return this.request('DELETE', `/v1/practice-areas/${id}/members/${partyId}`, null, callback);
  }

  // ===== END PRACTICE AREAS =====

  // ===== POSITIONS =====

  static async getPositions(callback?: any) {
    return this.request('GET', '/v1/positions', null, callback);
  }

  static async getPositionsDropdown(callback?: any) {
    return this.request('GET', '/v1/positions/dropdown', null, callback);
  }

  static async getPositionById(id: number | string, callback?: any) {
    return this.request('GET', `/v1/positions/${id}`, null, callback);
  }

  static async createPosition(data: any, callback?: any) {
    return this.request('POST', '/v1/positions', data, callback);
  }

  static async updatePosition(id: number | string, data: any, callback?: any) {
    return this.request('PUT', `/v1/positions/${id}`, data, callback);
  }

  static async deletePosition(id: number | string, callback?: any) {
    return this.request('DELETE', `/v1/positions/${id}`, null, callback);
  }

  static async setPositionActive(id: number | string, isActive: boolean, callback?: any) {
    return this.request('PATCH', `/v1/positions/${id}/active`, { isActive }, callback);
  }

  static async getPositionMembers(id: number | string, callback?: any, extraConfig?: Record<string, any>) {
    return this.request('GET', `/v1/positions/${id}/members`, null, callback, 'application/json', undefined, true, extraConfig);
  }

  static async addPositionMember(id: number | string, data: any, callback?: any) {
    return this.request('POST', `/v1/positions/${id}/members`, data, callback);
  }

  static async removePositionMember(id: number | string, partyId: number | string, callback?: any) {
    return this.request('DELETE', `/v1/positions/${id}/members/${partyId}`, null, callback);
  }

  // ---- Client Position Assignments ----

  static async getClientPositionAssignments(clientId: number, callback?: any) {
    return this.request('GET', `/v1/client/${clientId}/position-assignments`, null, callback);
  }

  static async addClientPositionAssignment(
    clientId: number,
    body: { positionId: number; partyId: number; priority?: number },
    callback?: any
  ) {
    return this.request('POST', `/v1/client/${clientId}/position-assignments`, body, callback);
  }

  static async removeClientPositionAssignment(
    clientId: number,
    body: { positionId: number; partyId: number },
    callback?: any
  ) {
    // DELETE with body — call axiosInstance directly because request() strips body on DELETE.
    // Uses axiosInstance (not bare Axios) to get the timeout default and error interceptor.
    try {
      const headers = BaseAPI.getAuthHeaders('application/json');
      const response = await axiosInstance({
        method: 'DELETE',
        url: BaseAPI.buildAPIURL(`/v1/client/${clientId}/position-assignments`),
        headers,
        data: body
      });
      if (callback) callback(response);
      return response;
    } catch (error: any) {
      if (error.response) {
        const errorResponse: any = {
          status: error.response.status,
          message:
            error.response.data?.message ||
            error.response.data?.error ||
            error.response.data?.reason ||
            'General server error'
        };
        if (callback) callback(errorResponse);
        return errorResponse;
      }
      return undefined;
    }
  }

  // ---- End Client Position Assignments ----

  static async getRoleDefinitions(callback?: any) {
    return this.request('GET', '/v1/role-definitions', null, callback);
  }

  // ===== END POSITIONS =====

  // ===== PARTY SPECIALIZATIONS =====

  static async getPartySpecializations(partyId: number | string, callback?: any) {
    return this.request('GET', `/v1/party/${partyId}/specializations`, null, callback);
  }

  static async addPartySpecialization(partyId: number | string, data: any, callback?: any) {
    return this.request('POST', `/v1/party/${partyId}/specializations`, data, callback);
  }

  static async removePartySpecialization(partyId: number | string, specId: number | string, callback?: any) {
    return this.request('DELETE', `/v1/party/${partyId}/specializations/${specId}`, null, callback);
  }

  static async getSpecializationParties(specId: number | string, callback?: any) {
    return this.request('GET', `/v1/specializations/${specId}/parties`, null, callback);
  }

  // ===== END PARTY SPECIALIZATIONS =====

  static async setPracticeAreaFiltering(enabled: boolean, callback?: any) {
    return this.request('PATCH', '/tenants/config/practice-area-filtering', { enabled }, callback);
  }

  static async setDefaultVisibilityMode(mode: string, callback?: any) {
    return this.request('PATCH', '/tenants/config/default-visibility-mode', { mode }, callback);
  }
}
